import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../components/ui/SharedUI';
import { demoRepository } from '../repository/demoRepository';
import { pocketbaseService } from '../services/pocketbaseService';
import { calculations } from '../utils/calculations';
import { useFeedback } from '../components/ui/FeedbackUI';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatPdfCurrency = (value) => calculations.formatCurrencyAUD(value);
const formatPdfPercent = (value) => calculations.formatPercentage(value, false);
const formatPdfDate = (ts) => ts ? calculations.formatDateAU(ts) : '-';
const safeText = (v) => v || '-';
const normaliseStatus = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
const getDashboardDateUrgency = (days) => {
  if (days < 0) {
    return {
      ring: '#dc2626',
      text: 'text-red-700',
      soft: 'bg-red-50',
      border: 'border-red-200',
      badge: 'bg-red-50 text-red-700 border-red-200'
    };
  }
  if (days <= 7) {
    return {
      ring: '#ef4444',
      text: 'text-red-700',
      soft: 'bg-red-50',
      border: 'border-red-200',
      badge: 'bg-red-50 text-red-700 border-red-200'
    };
  }
  if (days <= 30) {
    return {
      ring: '#f59e0b',
      text: 'text-amber-700',
      soft: 'bg-amber-50',
      border: 'border-amber-200',
      badge: 'bg-amber-50 text-amber-700 border-amber-200'
    };
  }
  if (days <= 90) {
    return {
      ring: '#2563eb',
      text: 'text-blue-700',
      soft: 'bg-blue-50',
      border: 'border-blue-200',
      badge: 'bg-blue-50 text-blue-700 border-blue-200'
    };
  }
  return {
    ring: '#059669',
    text: 'text-emerald-700',
    soft: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
};
const getTransactionFinancialYear = (transaction) => transaction?.financialYear || (transaction?.date ? calculations.getAustralianFinancialYear(transaction.date) : '');
const roundMoney = (value) => calculations.roundMoney(Number(value) || 0);

const buildDashboardMonths = (financialYear) => {
  const months = [];
  const match = String(financialYear || '').match(/^(\d{4})-\d{2}$/);
  if (match) {
    const startYear = Number(match[1]);
    for (let i = 0; i < 12; i++) {
      const d = new Date(startYear, 6 + i, 1);
      months.push({ month: d.toLocaleString('en-AU', { month: 'short', year: 'numeric' }), timestamp: d.getTime() });
    }
    return months;
  }

  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ month: d.toLocaleString('en-AU', { month: 'short', year: 'numeric' }), timestamp: d.getTime() });
  }
  return months;
};

const buildPreviousFinancialYears = (count = 15) => {
  const currentFy = calculations.getAustralianFinancialYear(new Date());
  const currentStartYear = Number(String(currentFy).split('-')[0]);
  if (!Number.isFinite(currentStartYear)) return [];
  return Array.from({ length: count }, (_, index) => {
    const startYear = currentStartYear - index - 1;
    return `${startYear}-${String(startYear + 1).slice(-2)}`;
  });
};

const HISTORICAL_FY_OPTIONS = buildPreviousFinancialYears(15);
const DASHBOARD_PROPERTY_ORDER_KEY = 'mpp.dashboard.property-order.v1';

const getHistoricalFyStartTimestamp = (financialYear) => {
  const match = String(financialYear || '').match(/^(\d{4})-\d{2}$/);
  if (!match) return null;
  const startYear = Number(match[1]);
  if (!Number.isFinite(startYear)) return null;
  // Use the first day of the completed FY so an annual aggregate is not mistaken
  // for a current trailing-12-month cash event. The explicit financialYear field
  // remains authoritative for FY reporting.
  return new Date(startYear, 6, 1, 12, 0, 0, 0).getTime();
};

const humaniseCategory = (value) => String(value || 'Uncategorised')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const getPreviousFinancialYear = (financialYear) => {
  const match = String(financialYear || '').match(/^(\d{4})-\d{2}$/);
  if (!match) return '';
  const startYear = Number(match[1]) - 1;
  if (!Number.isFinite(startYear)) return '';
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const formatDashboardCurrency = (value) => {
  const rounded = roundMoney(Number(value) || 0);
  const hasCents = Math.abs(rounded - Math.round(rounded)) >= 0.005;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2
  }).format(rounded);
};

const formatSignedDashboardCurrency = (value) => {
  const amount = roundMoney(Number(value) || 0);
  if (amount > 0) return `+${formatDashboardCurrency(amount)}`;
  if (amount < 0) return `-${formatDashboardCurrency(Math.abs(amount))}`;
  return formatDashboardCurrency(0);
};

const getPropertyFinancialDetails = (property) => {
  const financial = property?.metadata?.financial || {};
  return {
    loanType: property?.loanType || financial.loanType || '',
    repaymentType: property?.repaymentType || financial.repaymentType || '',
    interestRate: Number(property?.interestRate ?? financial.interestRate ?? 0) || 0
  };
};

const isOwnerOccupiedProperty = (property) => calculations.isOwnerOccupiedProperty(property);

const getPropertyImageUrl = (property) => property?.imageUrl
  || property?.photoUrl
  || property?.coverImage
  || property?.metadata?.imageUrl
  || property?.metadata?.photoUrl
  || property?.metadata?.coverImage
  || '';

const hasRecordedCashActivity = (summary) => {
  if (!summary) return false;
  return [
    summary.actualOperatingIncome,
    summary.actualOperatingExpenses,
    summary.financingCashOutflow,
    summary.loanInterestPaid,
    summary.loanPrincipalPaid,
    summary.loanFeesPaid
  ].some(value => Math.abs(Number(value) || 0) > 0.004);
};

const isCashFlowCalculationComplete = (summary) => Boolean(
  summary
  && summary.financeCashFlowComplete !== false
  && Number(summary.performanceReviewRequiredCount || 0) === 0
  && Number(summary.scheduleOtherDeductions || 0) === 0
  && summary.formulaChecksPassed !== false
);

const getCashFlowStatusConfig = ({ ownerOccupied, cashFlow, hasActivity }) => {
  if (ownerOccupied) {
    return {
      label: 'OWNER OCCUPIED',
      icon: 'Home',
      banner: 'bg-blue-50 text-blue-800 border-blue-200',
      amount: 'text-blue-700',
      border: 'border-blue-200 hover:border-blue-300'
    };
  }
  if (!hasActivity && Math.abs(cashFlow) < 0.005) {
    return {
      label: 'NO RECORDED CASH RESULT',
      icon: 'Minus',
      banner: 'bg-slate-50 text-slate-700 border-slate-200',
      amount: 'text-slate-800',
      border: 'border-slate-200 hover:border-slate-300'
    };
  }
  if (cashFlow > 0.004) {
    return {
      label: 'CASH-FLOW POSITIVE',
      icon: 'TrendingUp',
      banner: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      amount: 'text-emerald-700',
      border: 'border-emerald-200 hover:border-emerald-300'
    };
  }
  if (cashFlow < -0.004) {
    return {
      label: 'CASH-FLOW NEGATIVE',
      icon: 'TrendingDown',
      banner: 'bg-red-50 text-red-800 border-red-200',
      amount: 'text-red-700',
      border: 'border-red-200 hover:border-red-300'
    };
  }
  return {
    label: 'BREAK EVEN',
    icon: 'Minus',
    banner: 'bg-slate-50 text-slate-700 border-slate-200',
    amount: 'text-slate-800',
    border: 'border-slate-200 hover:border-slate-300'
  };
};

const PropertyMetric = ({ label, value, valueClassName = 'text-slate-900' }) => (
  <div className="min-w-0 rounded-lg border border-slate-100 bg-slate-50/70 px-2.5 py-2.5 sm:px-3">
    <div className="min-h-[1.5rem] break-words text-[9px] font-semibold uppercase leading-3 tracking-wide text-slate-400 sm:text-[10px]" title={label}>{label}</div>
    <div className={`mt-1 break-words text-sm font-semibold leading-5 ${valueClassName}`} title={String(value)}>{value}</div>
  </div>
);

const PropertySnapshotCard = ({
  property,
  summary,
  previousSummary,
  financialYear,
  previousFinancialYear,
  isCurrentFinancialYear,
  onOpen
}) => {
  const financial = getPropertyFinancialDetails(property);
  const ownerOccupied = isOwnerOccupiedProperty(property, financial.loanType);
  const repaymentType = financial.repaymentType || 'Repayment not set';
  const repaymentKey = normaliseStatus(repaymentType);
  const isPrincipalAndInterest = repaymentKey.includes('principal') && repaymentKey.includes('interest');
  const currentCashFlow = roundMoney(summary?.cashFlowAfterFinance ?? summary?.netCashFlow ?? 0);
  const previousCashFlow = roundMoney(previousSummary?.cashFlowAfterFinance ?? previousSummary?.netCashFlow ?? 0);
  const currentHasActivity = hasRecordedCashActivity(summary);
  const previousHasActivity = hasRecordedCashActivity(previousSummary);
  const currentCalculationComplete = isCashFlowCalculationComplete(summary);
  const previousCalculationComplete = isCashFlowCalculationComplete(previousSummary);
  const status = getCashFlowStatusConfig({ ownerOccupied, cashFlow: currentCashFlow, hasActivity: currentHasActivity });
  const periodLabel = `FY ${financialYear}${isCurrentFinancialYear ? ' to date' : ''}`;
  const propertyImageUrl = getPropertyImageUrl(property);
  const occupancyLabel = summary?.occupancyStatus || ((Number(summary?.occupancyRate) || 0) > 0 ? 'Occupied' : 'Vacant');
  const descriptor = [
    ownerOccupied ? 'Owner Occupied' : (normaliseStatus(financial.loanType).includes('investment') ? 'Investment Property' : (property?.propertyType || 'Property')),
    repaymentType,
    ownerOccupied ? null : occupancyLabel
  ].filter(Boolean).join(' · ');
  const locationLabel = [property?.suburb, property?.state].filter(Boolean).join(', ');
  const indicativePosition = calculations.getPropertyIndicativeCashFlowPosition(property?.id);
  const nextContractedPosition = !ownerOccupied ? calculations.getPropertyNextContractedPosition(property?.id) : null;
  const indicativeStatusLabel = indicativePosition.complete
    ? indicativePosition.classification === 'positive' ? 'Positive cash flow' : indicativePosition.classification === 'negative' ? 'Negative cash flow' : 'Neutral cash flow'
    : 'Projection incomplete';
  const indicativeStatusClass = indicativePosition.classification === 'positive'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : indicativePosition.classification === 'negative'
      ? 'border-red-200 bg-red-50 text-red-700'
      : 'border-amber-200 bg-amber-50 text-amber-700';
  const currentPositionMetrics = ownerOccupied
    ? [
        ['Market Value', formatDashboardCurrency(property?.currentMarketValue || 0)],
        ['Loan', formatDashboardCurrency(summary?.totalLoanBalance || 0)],
        ['Equity', formatDashboardCurrency(summary?.equity || 0)],
        ['LVR', calculations.formatPercentage(summary?.lvr || 0)],
        ['Current Installment', indicativePosition.loan.installmentComplete ? formatDashboardCurrency(indicativePosition.loan.monthlyInstallment) + '/mo' : 'Needs rate/term']
      ]
    : [
        ['Market Value', formatDashboardCurrency(property?.currentMarketValue || 0)],
        ['Loan', formatDashboardCurrency(summary?.totalLoanBalance || 0)],
        ['Equity', formatDashboardCurrency(summary?.equity || 0)],
        ['LVR', calculations.formatPercentage(summary?.lvr || 0)],
        ['Anticipated Gross Rent', indicativePosition.rent.rentComplete ? formatDashboardCurrency(indicativePosition.rent.monthlyGrossRent) + '/mo' : 'Needs rent data'],
        ['Anticipated Net Rent', indicativePosition.rent.rentComplete ? formatDashboardCurrency(indicativePosition.rent.monthlyNetRent) + '/mo' : 'Needs rent data'],
        ['Current Installment', indicativePosition.loan.installmentComplete ? formatDashboardCurrency(indicativePosition.loan.monthlyInstallment) + '/mo' : 'Needs rate/term'],
        ['Indicative Cash Flow', indicativePosition.complete ? formatSignedDashboardCurrency(indicativePosition.monthlyCashFlow) + '/mo' : 'Needs data']
      ];
  const selectedPerformanceMetrics = ownerOccupied
    ? [
        ['Mortgage Outflow', formatDashboardCurrency(summary?.financingCashOutflow || 0)],
        ['Interest Paid', summary?.financeSplitComplete ? formatDashboardCurrency(summary?.loanInterestPaid || 0) : 'Not available'],
        ['Principal Repaid', summary?.financeSplitComplete ? formatDashboardCurrency(summary?.loanPrincipalPaid || 0) : 'Not available'],
        ['Repayment', repaymentType]
      ]
    : [
        ['Rent Received', formatDashboardCurrency(summary?.actualOperatingIncome || 0)],
        ['Operating Expenses', formatDashboardCurrency(summary?.actualOperatingExpenses || 0)],
        ['NOI', formatDashboardCurrency(summary?.netOperatingIncome || 0)],
        ['Finance Outflow', formatDashboardCurrency(summary?.financingCashOutflow || 0)]
      ];
  const comparisonDelta = roundMoney(currentCashFlow - previousCashFlow);
  const showComparableDelta = !isCurrentFinancialYear && currentHasActivity && previousHasActivity;
  const comparisonTone = comparisonDelta > 0.004
    ? { text: 'Improved', icon: 'TrendingUp', className: 'text-emerald-700 bg-emerald-50 border-emerald-100' }
    : comparisonDelta < -0.004
      ? { text: 'Worsened', icon: 'TrendingDown', className: 'text-red-700 bg-red-50 border-red-100' }
      : { text: 'Unchanged', icon: 'Minus', className: 'text-slate-600 bg-slate-50 border-slate-100' };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`Open ${property?.name || 'property'} details`}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      className={`group h-full overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 cursor-pointer ${status.border}`}
    >
      <div className={`flex items-center justify-between gap-3 border-b px-4 py-2.5 ${status.banner}`}>
        <div className="flex min-w-0 items-center gap-2 text-xs font-bold tracking-wide">
          <SafeIcon icon={status.icon} className="text-base flex-shrink-0" />
          <span className="truncate">{status.label}</span>
        </div>
        <span className={`flex-shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${currentCalculationComplete ? 'border-emerald-200 bg-white/70 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
          {currentCalculationComplete ? 'Complete' : 'Needs review'}
        </span>
      </div>

      <div className="flex items-start gap-3 px-4 pt-4">
        <div className="relative h-14 w-16 flex-shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-100 to-slate-200 md:h-16 md:w-20 2xl:h-20 2xl:w-24">
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            <SafeIcon icon="Home" className="text-2xl" />
          </div>
          {propertyImageUrl && (
            <img
              src={propertyImageUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              onError={(event) => { event.currentTarget.style.display = 'none'; }}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-base font-bold leading-5 text-slate-900 group-hover:text-amber-800 sm:text-lg">{property?.name || 'Unnamed Property'}</h3>
              {locationLabel && <p className="mt-0.5 text-xs text-slate-400">{locationLabel}</p>}
            </div>
            <SafeIcon icon="ChevronRight" className="mt-0.5 flex-shrink-0 text-lg text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-amber-600" />
          </div>
          <p className="mt-1 text-[11px] leading-4 text-slate-500 sm:text-xs">{descriptor}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 2xl:grid-cols-1">
        <div className="p-4 md:col-span-2 md:border-r md:border-slate-100 2xl:col-span-1 2xl:border-r-0 2xl:border-b 2xl:border-slate-100">
          {ownerOccupied ? (
            <div className="flex h-full flex-col justify-center rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-4 text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-600">Mortgage cash outflow</div>
              <div className="mt-2 text-2xl font-bold text-blue-800">{formatDashboardCurrency(summary?.financingCashOutflow || 0)}</div>
              <div className="mt-1 text-xs font-medium text-blue-700">{periodLabel}</div>
              <p className="mt-3 text-[11px] leading-4 text-blue-700/80">Owner-occupied property — not classified as investment profit or loss.</p>
            </div>
          ) : (
            <div className="text-center md:text-left 2xl:text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Recorded cash result</div>
              <div className={`mt-1 text-3xl font-bold tracking-tight ${status.amount}`}>{formatSignedDashboardCurrency(currentCashFlow)}</div>
              <div className="mt-0.5 text-sm font-medium text-slate-600">{periodLabel}</div>
              <p className="mt-1 text-[10px] leading-4 text-slate-400">Before personal income-tax outcomes such as an ATO refund.</p>

              <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 text-xs">
                <div className="flex items-center justify-between gap-2 text-slate-500">
                  <span>Previous FY {previousFinancialYear}</span>
                  <span className={`font-semibold ${previousCashFlow > 0 ? 'text-emerald-700' : previousCashFlow < 0 ? 'text-red-700' : 'text-slate-700'}`}>
                    {previousHasActivity ? formatSignedDashboardCurrency(previousCashFlow) : 'No recorded result'}
                  </span>
                </div>
                {previousHasActivity && !previousCalculationComplete && (
                  <div className="mt-1 text-[10px] text-amber-700">Previous FY calculation requires review.</div>
                )}
              </div>

              {showComparableDelta ? (
                <div className={`mt-2 flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-2 text-[11px] font-semibold md:justify-start 2xl:justify-center ${comparisonTone.className}`}>
                  <SafeIcon icon={comparisonTone.icon} className="text-sm" />
                  <span>{comparisonTone.text} by {formatDashboardCurrency(Math.abs(comparisonDelta))} vs previous FY</span>
                </div>
              ) : isCurrentFinancialYear && previousHasActivity ? (
                <div className="mt-2 rounded-lg border border-slate-100 bg-white px-2.5 py-2 text-center text-[10px] leading-4 text-slate-500 md:text-left 2xl:text-center">
                  Current FY is still in progress, so the previous full FY is shown for context rather than as a like-for-like trend.
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="p-4 md:col-span-3 2xl:col-span-1">
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
              Current Position
              <SafeIcon icon="Info" className="text-xs text-slate-300" />
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(118px,1fr))] gap-2">
              {currentPositionMetrics.map(([label, value]) => {
                const valueClassName = label === 'Equity'
                  ? 'text-emerald-700'
                  : label === 'Indicative Cash Flow'
                    ? indicativePosition.classification === 'positive' ? 'text-emerald-700' : indicativePosition.classification === 'negative' ? 'text-red-700' : 'text-amber-700'
                    : 'text-slate-900';
                return <PropertyMetric key={label} label={label} value={value} valueClassName={valueClassName} />;
              })}
            </div>
            {!ownerOccupied && (
              <div className="mt-2">
                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold ${indicativeStatusClass}`}>{indicativeStatusLabel}</span>
                {indicativePosition.complete && <span className="ml-2 text-[10px] text-slate-500">{formatSignedDashboardCurrency(indicativePosition.annualCashFlow)} / year</span>}
              </div>
            )}
            {!ownerOccupied && nextContractedPosition?.rent?.rentComplete && (nextContractedPosition.changedGrossRent || nextContractedPosition.changedNetRent || nextContractedPosition.changedCashFlow) && (
              <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[10px] leading-4 text-blue-800">
                <span className="font-semibold">Next contracted position from {calculations.formatDateAU(nextContractedPosition.effectiveDate)}:</span> {formatDashboardCurrency(nextContractedPosition.rent.monthlyGrossRent)} gross / mo · {formatDashboardCurrency(nextContractedPosition.rent.monthlyNetRent)} net / mo{nextContractedPosition.complete ? ` · ${formatSignedDashboardCurrency(nextContractedPosition.monthlyCashFlow)} cash flow / mo` : ''}
              </div>
            )}
            <p className="mt-2 text-[9px] leading-4 text-slate-400">
              {ownerOccupied
                ? 'Installment is formula-driven from the current recorded loan balance, rate, repayment type and remaining term.'
                : 'Indicative only: anticipated net rent minus current formula-driven installment. Council rates, water, strata, insurance, maintenance and other costs are excluded unless recorded in rent schedule deductions.'}
            </p>
          </div>

          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                {ownerOccupied ? `Finance · ${periodLabel}` : `Performance · ${periodLabel}`}
                <SafeIcon icon="Info" className="text-xs text-slate-300" />
              </div>
              {!ownerOccupied && Number(summary?.scheduleRentUsed || 0) > 0 && (
                <span className="rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[9px] font-semibold text-blue-700" title={summary?.calculationHelp?.operatingIncome || 'Realised rent is sourced from Paid rent-schedule rows.'}>
                  {summary?.paidRentScheduleCount || 0} Paid schedule row{Number(summary?.paidRentScheduleCount || 0)===1 ? '' : 's'}
                </span>
              )}
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(118px,1fr))] gap-2">
              {selectedPerformanceMetrics.map(([label, value]) => {
                const valueClassName = !ownerOccupied && label === 'NOI'
                  ? (Number(summary?.netOperatingIncome || 0) >= 0 ? 'text-emerald-700' : 'text-red-700')
                  : !ownerOccupied && label === 'Finance Outflow'
                    ? 'text-red-700'
                    : 'text-slate-900';
                return <PropertyMetric key={label} label={label} value={value} valueClassName={valueClassName} />;
              })}
            </div>
          </div>

          {!ownerOccupied && isPrincipalAndInterest && summary?.financeSplitComplete && Number(summary?.loanPrincipalPaid || 0) > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-[11px] leading-4 text-amber-900">
              <SafeIcon icon="ArrowDownCircle" className="mt-0.5 flex-shrink-0 text-sm" />
              <div><span className="font-semibold">Principal repaid: {formatDashboardCurrency(summary.loanPrincipalPaid)}</span> — debt reduction/equity building, not an operating expense.</div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 border-t border-slate-100 bg-slate-50/50">
        {ownerOccupied ? (
          <>
            <div className="min-w-0 px-2 py-3 text-center">
              <SafeIcon icon="Home" className="mx-auto text-sm text-blue-600" />
              <div className="mt-1 truncate text-[9px] uppercase tracking-wide text-slate-400">Property Use</div>
              <div className="truncate text-[11px] font-semibold text-slate-700">Owner Occupied</div>
            </div>
            <div className="min-w-0 border-l border-slate-100 px-2 py-3 text-center">
              <SafeIcon icon="CreditCard" className="mx-auto text-sm text-slate-500" />
              <div className="mt-1 truncate text-[9px] uppercase tracking-wide text-slate-400">Repayment</div>
              <div className="truncate text-[11px] font-semibold text-slate-700">{repaymentType}</div>
            </div>
          </>
        ) : (
          <>
            <div className="min-w-0 px-2 py-3 text-center">
              <SafeIcon icon="Tag" className="mx-auto text-sm text-amber-600" />
              <div className="mt-1 truncate text-[9px] uppercase tracking-wide text-slate-400">Current Expected Rent</div>
              <div className="truncate text-[11px] font-semibold text-slate-700">{formatDashboardCurrency(summary?.annualRent || 0)} p.a.</div>
            </div>
            <div className="min-w-0 border-l border-slate-100 px-2 py-3 text-center">
              <SafeIcon icon="Users" className="mx-auto text-sm text-slate-500" />
              <div className="mt-1 truncate text-[9px] uppercase tracking-wide text-slate-400">Current Occupancy</div>
              <div className="truncate text-[11px] font-semibold text-slate-700">{calculations.formatPercentage(summary?.occupancyRate || 0)}</div>
            </div>
          </>
        )}
        <div className="min-w-0 border-l border-slate-100 px-2 py-3 text-center">
          <SafeIcon icon={currentCalculationComplete ? 'CheckCircle' : 'AlertCircle'} className={`mx-auto text-sm ${currentCalculationComplete ? 'text-emerald-600' : 'text-amber-600'}`} />
          <div className="mt-1 truncate text-[9px] uppercase tracking-wide text-slate-400">Calculations</div>
          <div className={`truncate text-[11px] font-semibold ${currentCalculationComplete ? 'text-emerald-700' : 'text-amber-700'}`}>{currentCalculationComplete ? 'Complete' : 'Review'}</div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-1.5 border-t border-slate-100 px-4 py-3 text-xs font-semibold text-amber-700 transition-colors group-hover:bg-amber-50/40 group-hover:text-amber-800">
        View Property <SafeIcon icon="ArrowRight" className="text-sm" />
      </div>
    </article>
  );
};

const MobileDashboardMetric = ({ icon, label, value, supportingText, tone = 'slate' }) => {
  const tones = {
    slate: { icon: 'bg-slate-100 text-slate-600', value: 'text-slate-900' },
    blue: { icon: 'bg-blue-50 text-blue-600', value: 'text-blue-900' },
    emerald: { icon: 'bg-emerald-50 text-emerald-600', value: 'text-emerald-800' },
    amber: { icon: 'bg-amber-50 text-amber-600', value: 'text-amber-900' },
    violet: { icon: 'bg-violet-50 text-violet-600', value: 'text-violet-900' },
    red: { icon: 'bg-red-50 text-red-600', value: 'text-red-800' }
  };
  const style = tones[tone] || tones.slate;
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl sm:h-10 sm:w-10 sm:rounded-2xl ${style.icon}`}>
          <SafeIcon icon={icon} className="text-lg sm:text-xl" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-[10px] font-semibold leading-4 text-slate-500 sm:text-xs">{label}</div>
          <div className={`mt-0.5 whitespace-nowrap text-base font-bold leading-tight sm:text-xl ${style.value}`}>{value}</div>
          {supportingText ? <div className="mt-1 line-clamp-2 text-[10px] leading-4 text-slate-400 sm:text-[11px]">{supportingText}</div> : null}
        </div>
      </div>
    </div>
  );
};

const MobileCountdownCard = ({ event, onOpen }) => {
  const boundedDays = Math.max(0, Math.min(90, Number(event?.days || 0)));
  const progress = Math.max(10, Math.min(100, ((90 - boundedDays) / 90) * 100));
  const urgency = getDashboardDateUrgency(Number(event?.days || 0));
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group flex min-w-0 items-center gap-3 rounded-2xl border ${urgency.border} bg-white p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-500`}
    >
      <div
        className="relative flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full"
        style={{ background: `conic-gradient(${urgency.ring} ${progress * 3.6}deg,#e2e8f0 0deg)` }}
        aria-label={`${event.days} days remaining`}
      >
        <div className="absolute inset-[5px] rounded-full bg-white" />
        <div className="relative text-center">
          <div className={`text-lg font-bold leading-none ${urgency.text}`}>{event.days}</div>
          <div className={`mt-0.5 text-[9px] font-semibold uppercase tracking-wide ${urgency.text}`}>days</div>
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-slate-900">{event.propertyName}</div>
        <div className="mt-0.5 truncate text-xs font-medium text-slate-500">{event.label}</div>
        <div className="mt-1 text-[11px] text-slate-400">{calculations.formatDateAU(event.date)}</div>
      </div>
      <SafeIcon icon="ChevronRight" className="flex-shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-amber-600" />
    </button>
  );
};

const MobilePropertyOverviewCard = ({ property, summary, onOpen }) => {
  const imageUrl = getPropertyImageUrl(property);
  const occupancy = calculations.formatPercentage(summary?.occupancyRate || 0);
  const financial = getPropertyFinancialDetails(property);
  const ownerOccupied = isOwnerOccupiedProperty(property, financial.loanType);
  const projection = calculations.getPropertyIndicativeCashFlowPosition(property?.id);
  const nextContractedPosition = !ownerOccupied ? calculations.getPropertyNextContractedPosition(property?.id) : null;
  const projectionLabel = ownerOccupied
    ? 'PPR'
    : projection.complete
      ? projection.classification === 'positive' ? 'Positive cash flow' : projection.classification === 'negative' ? 'Negative cash flow' : 'Neutral cash flow'
      : 'Projection incomplete';
  const projectionClass = ownerOccupied
    ? 'border-blue-100 bg-blue-50 text-blue-700'
    : projection.classification === 'positive'
      ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
      : projection.classification === 'negative'
        ? 'border-red-100 bg-red-50 text-red-700'
        : 'border-amber-100 bg-amber-50 text-amber-700';
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group w-full overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-500"
    >
      <div className="grid min-h-[116px] grid-cols-[108px_minmax(0,1fr)] sm:grid-cols-[132px_minmax(0,1fr)]">
        <div className="bg-slate-100">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full items-center justify-center text-slate-300">
              <SafeIcon icon="Home" className="text-3xl" />
            </div>
          )}
        </div>
        <div className="min-w-0 p-3 sm:p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-bold text-slate-900 sm:text-base">{property?.name || 'Property'}</div>
              <div className="mt-0.5 truncate text-[11px] text-slate-500">{[property?.suburb, property?.state].filter(Boolean).join(', ') || property?.address || 'Property portfolio'}</div>
            </div>
            <SafeIcon icon="ChevronRight" className="mt-0.5 flex-shrink-0 text-slate-300 group-hover:text-amber-600" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Market Value</div>
              <div className="mt-0.5 truncate text-xs font-bold text-slate-800 sm:text-sm">{formatDashboardCurrency(property?.currentMarketValue || 0)}</div>
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Occupancy</div>
              <div className="mt-0.5 text-xs font-bold text-slate-800 sm:text-sm">{occupancy}</div>
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Annual Rent</div>
              <div className="mt-0.5 truncate text-xs font-bold text-slate-800 sm:text-sm">{formatDashboardCurrency(summary?.annualRent || 0)}</div>
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Recorded FY Cash Flow</div>
              <div className={`mt-0.5 truncate text-xs font-bold sm:text-sm ${Number(summary?.cashFlowAfterFinance || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatSignedDashboardCurrency(summary?.cashFlowAfterFinance || 0)}</div>
            </div>
          </div>
          <div className={`mt-3 rounded-lg border px-2.5 py-2 ${projectionClass}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[9px] font-bold uppercase tracking-wide">Current indicative position</span>
              <span className="text-[9px] font-bold">{projectionLabel}</span>
            </div>
            <div className="mt-1 text-[10px] leading-4">
              {ownerOccupied
                ? `${formatDashboardCurrency(projection.loan.monthlyInstallment)} / month installment · ${formatDashboardCurrency(projection.loan.monthlyInstallment*12)} / year`
                : projection.complete
                  ? `${formatDashboardCurrency(projection.rent.monthlyNetRent)} net rent − ${formatDashboardCurrency(projection.loan.monthlyInstallment)} installment = ${formatSignedDashboardCurrency(projection.monthlyCashFlow)} / month (${formatSignedDashboardCurrency(projection.annualCashFlow)} / year)`
                  : 'Complete current rent schedule and loan rate/term details.'}
            </div>
          </div>
          {nextContractedPosition?.rent?.rentComplete && (nextContractedPosition.changedGrossRent || nextContractedPosition.changedNetRent || nextContractedPosition.changedCashFlow) && (
            <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-[10px] leading-4 text-blue-800">
              <span className="font-semibold">From {calculations.formatDateAU(nextContractedPosition.effectiveDate)}:</span> {formatDashboardCurrency(nextContractedPosition.rent.monthlyGrossRent)} gross / mo · {formatDashboardCurrency(nextContractedPosition.rent.monthlyNetRent)} net / mo
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

const DesktopKpiCard = ({ icon, label, value, supportingText, tone = 'amber' }) => {
  const tones = {
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    violet: 'bg-violet-50 text-violet-700 border-violet-100',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    red: 'bg-red-50 text-red-700 border-red-100'
  };
  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white px-3.5 py-3 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border ${tones[tone] || tones.amber}`}>
          <SafeIcon icon={icon} className="text-lg" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-semibold text-slate-500" title={label}>{label}</div>
          <div className="mt-0.5 whitespace-nowrap text-xl font-bold leading-tight text-slate-950" title={String(value)}>{value}</div>
          {supportingText ? <div className="mt-1 truncate text-[10px] text-slate-400" title={supportingText}>{supportingText}</div> : null}
        </div>
      </div>
    </div>
  );
};

const DesktopPropertyPerformanceCard = ({
  property,
  summary,
  onOpen,
  pinned = false,
  draggable = false,
  isDragging = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}) => {
  const imageUrl = getPropertyImageUrl(property);
  const financial = getPropertyFinancialDetails(property);
  const ownerOccupied = isOwnerOccupiedProperty(property, financial.loanType);
  const occupancy = calculations.formatPercentage(summary?.occupancyRate || 0);
  const location = [property?.suburb, property?.state].filter(Boolean).join(', ');
  const projection = calculations.getPropertyIndicativeCashFlowPosition(property?.id);
  const nextContractedPosition = !ownerOccupied ? calculations.getPropertyNextContractedPosition(property?.id) : null;
  const projectionValue = ownerOccupied
    ? formatDashboardCurrency(projection.loan.monthlyInstallment)
    : projection.complete ? formatSignedDashboardCurrency(projection.monthlyCashFlow) : 'Needs data';
  const projectionLabel = ownerOccupied ? 'PPR Installment / mo' : 'Indicative CF / mo';
  const projectionClass = ownerOccupied
    ? 'text-blue-700'
    : projection.classification === 'positive'
      ? 'text-emerald-700'
      : projection.classification === 'negative'
        ? 'text-red-700'
        : 'text-amber-700';
  const secondaryValue = ownerOccupied
    ? formatDashboardCurrency(summary?.equity || 0)
    : formatDashboardCurrency(summary?.annualRent || 0);
  const secondaryLabel = ownerOccupied ? 'Equity' : 'Annual Rent';

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`group relative min-w-0 overflow-hidden rounded-xl border bg-white shadow-sm transition-all ${isDragging ? 'scale-[0.99] border-amber-300 opacity-60' : 'border-slate-200 hover:-translate-y-0.5 hover:border-amber-200 hover:shadow-md'}`}
    >
      <button type="button" onClick={onOpen} className="grid w-full grid-cols-[92px_minmax(0,1fr)] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500">
        <div className="h-full min-h-[112px] bg-slate-100">
          {imageUrl ? <img src={imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" /> : <div className="flex h-full items-center justify-center text-slate-300"><SafeIcon icon="Home" className="text-2xl" /></div>}
        </div>
        <div className="min-w-0 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-xs font-bold text-slate-900" title={property?.name}>{property?.name || 'Property'}</div>
              <div className="mt-0.5 truncate text-[10px] text-slate-500">{location || property?.address || 'Property portfolio'}</div>
            </div>
            <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold ${ownerOccupied ? 'bg-blue-50 text-blue-700' : Number(summary?.occupancyRate || 0) > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {ownerOccupied ? 'PPR' : Number(summary?.occupancyRate || 0) > 0 ? 'Tenanted' : 'Vacant'}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="min-w-0">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Market Value</div>
              <div className="mt-0.5 truncate text-[11px] font-bold text-slate-800" title={formatDashboardCurrency(property?.currentMarketValue || 0)}>{formatDashboardCurrency(property?.currentMarketValue || 0)}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{secondaryLabel}</div>
              <div className="mt-0.5 truncate text-[11px] font-bold text-slate-800" title={secondaryValue}>{secondaryValue}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Occupancy</div>
              <div className="mt-0.5 text-[11px] font-semibold text-slate-700">{ownerOccupied ? 'Owner occupied' : occupancy}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{projectionLabel}</div>
              <div className={`mt-0.5 truncate text-[11px] font-bold ${projectionClass}`} title={projectionValue}>{projectionValue}</div>
            </div>
          </div>
          {nextContractedPosition?.rent?.rentComplete && (nextContractedPosition.changedGrossRent || nextContractedPosition.changedNetRent || nextContractedPosition.changedCashFlow) && (
            <div className="mt-2 truncate text-[9px] font-medium text-blue-700" title={`Next contracted position from ${calculations.formatDateAU(nextContractedPosition.effectiveDate)}`}>
              Next {calculations.formatDateAU(nextContractedPosition.effectiveDate)} · {formatDashboardCurrency(nextContractedPosition.rent.monthlyGrossRent)}/mo gross
            </div>
          )}
        </div>
      </button>
      <div className="absolute bottom-2 right-2 flex items-center gap-1">
        {pinned ? (
          <span className="inline-flex items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-1.5 py-1 text-[9px] font-semibold text-blue-700" title="Primary residence is pinned first">
            <SafeIcon icon="Lock" className="text-[10px]" /> First
          </span>
        ) : (
          <span className="inline-flex cursor-grab items-center gap-1 rounded-md border border-slate-200 bg-white/90 px-1.5 py-1 text-[9px] font-semibold text-slate-500 shadow-sm backdrop-blur-sm active:cursor-grabbing" title="Drag to reorder investment properties">
            <SafeIcon icon="Move" className="text-[10px]" /> Drag
          </span>
        )}
      </div>
    </div>
  );
};

const readMppTheme = () => {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset.mppTheme === 'dark' ? 'dark' : 'light';
};

const useMppTheme = () => {
  const [theme, setTheme] = useState(readMppTheme);
  useEffect(() => {
    const handleThemeChange = (event) => setTheme(event?.detail?.theme === 'dark' ? 'dark' : readMppTheme());
    window.addEventListener('mpp:theme-changed', handleThemeChange);
    return () => window.removeEventListener('mpp:theme-changed', handleThemeChange);
  }, []);
  return theme;
};

const themeChartAxis = (axis) => {
  if (!axis || typeof axis !== 'object') return axis;
  return {
    ...axis,
    axisLabel: { ...axis.axisLabel, color: '#cbd5e1' },
    axisLine: { ...axis.axisLine, lineStyle: { ...axis.axisLine?.lineStyle, color: '#475569' } },
    axisTick: { ...axis.axisTick, lineStyle: { ...axis.axisTick?.lineStyle, color: '#475569' } },
    splitLine: { ...axis.splitLine, lineStyle: { ...axis.splitLine?.lineStyle, color: '#26364d' } },
    nameTextStyle: { ...axis.nameTextStyle, color: '#dbe5f0' }
  };
};

const themeChartCollection = (value, mapper) => Array.isArray(value) ? value.map(mapper) : mapper(value);

const getDarkChartOption = (option) => {
  if (!option || typeof option !== 'object') return option;
  return {
    ...option,
    backgroundColor: 'transparent',
    textStyle: { ...option.textStyle, color: '#e2e8f0' },
    title: option.title
      ? themeChartCollection(option.title, (item) => ({
          ...item,
          textStyle: { ...item?.textStyle, color: '#f8fafc' },
          subtextStyle: { ...item?.subtextStyle, color: '#a9b8ca' }
        }))
      : option.title,
    legend: option.legend
      ? themeChartCollection(option.legend, (item) => ({
          ...item,
          textStyle: { ...item?.textStyle, color: '#dbe5f0' }
        }))
      : option.legend,
    tooltip: option.tooltip
      ? {
          ...option.tooltip,
          backgroundColor: '#0b1423',
          borderColor: '#42546d',
          textStyle: { ...option.tooltip?.textStyle, color: '#f8fafc' },
          axisPointer: {
            ...option.tooltip?.axisPointer,
            label: { ...option.tooltip?.axisPointer?.label, backgroundColor: '#172235', color: '#f8fafc' }
          },
          extraCssText: `${option.tooltip?.extraCssText || ''};box-shadow:0 12px 30px rgba(0,0,0,.34);border-radius:10px;`
        }
      : option.tooltip,
    xAxis: option.xAxis ? themeChartCollection(option.xAxis, themeChartAxis) : option.xAxis,
    yAxis: option.yAxis ? themeChartCollection(option.yAxis, themeChartAxis) : option.yAxis,
    radar: option.radar
      ? {
          ...option.radar,
          axisName: { ...option.radar?.axisName, color: '#dbe5f0' },
          splitLine: { ...option.radar?.splitLine, lineStyle: { ...option.radar?.splitLine?.lineStyle, color: '#26364d' } },
          splitArea: { ...option.radar?.splitArea, areaStyle: { ...option.radar?.splitArea?.areaStyle, color: ['rgba(17,28,46,.48)','rgba(15,23,42,.16)'] } },
          axisLine: { ...option.radar?.axisLine, lineStyle: { ...option.radar?.axisLine?.lineStyle, color: '#334155' } }
        }
      : option.radar,
    visualMap: option.visualMap
      ? themeChartCollection(option.visualMap, (item) => ({
          ...item,
          textStyle: { ...item?.textStyle, color: '#dbe5f0' }
        }))
      : option.visualMap,
    dataZoom: option.dataZoom
      ? themeChartCollection(option.dataZoom, (item) => ({
          ...item,
          textStyle: { ...item?.textStyle, color: '#dbe5f0' },
          borderColor: item?.borderColor || '#334155',
          fillerColor: item?.fillerColor || 'rgba(245,158,11,.14)',
          backgroundColor: item?.backgroundColor || 'rgba(15,23,42,.64)'
        }))
      : option.dataZoom,
    series: Array.isArray(option.series)
      ? option.series.map((series) => ({
          ...series,
          label: { ...series?.label, color: '#e2e8f0' },
          labelLine: { ...series?.labelLine, lineStyle: { ...series?.labelLine?.lineStyle, color: '#64748b' } },
          emphasis: {
            ...series?.emphasis,
            label: { ...series?.emphasis?.label, color: '#f8fafc' }
          },
          markLine: series?.markLine
            ? { ...series.markLine, label: { ...series.markLine?.label, color: '#e2e8f0' } }
            : series?.markLine,
          markPoint: series?.markPoint
            ? { ...series.markPoint, label: { ...series.markPoint?.label, color: '#e2e8f0' } }
            : series?.markPoint
        }))
      : option.series
  };
};

const DashboardChart = ({ option, hasData, emptyMessage, chartKey, compact = false }) => {
  const theme = useMppTheme();
  const themedOption = useMemo(() => theme === 'dark' ? getDarkChartOption(option) : option, [option, theme]);
  return (
    <div className={`mpp-echart-shell rounded-xl border border-slate-200 bg-white shadow-sm ${compact ? 'min-h-[286px] p-3 sm:p-4' : 'min-h-[342px] p-5'}`}>
      {hasData ? (
        <ReactECharts
          key={`${chartKey}-${theme}`}
          className="mpp-echart"
          option={themedOption}
          notMerge
          lazyUpdate
          opts={{ renderer: 'svg' }}
          style={{ height: compact ? '250px' : '300px', width: '100%' }}
        />
      ) : (
        <div className={`${compact ? 'h-[250px]' : 'h-[300px]'} flex flex-col items-center justify-center px-6 text-center`}>
          <SafeIcon icon="Info" className="mb-3 text-2xl text-slate-300" />
          <div className="text-sm font-semibold text-slate-700">No chart data for this selection</div>
          <div className="mt-1 max-w-sm text-xs text-slate-500">{emptyMessage}</div>
        </div>
      )}
    </div>
  );
};

const addPdfHeader = (doc, title, filters) => {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageW, 38, 'F');
  doc.setTextColor(245, 158, 11);
  doc.setFontSize(18);
  doc.text("Malhotra's Property Portfolio", 14, 14);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.text(title, 14, 24);
  doc.setFontSize(9);
  doc.setTextColor(200, 200, 200);
  const genDate = new Date().toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  doc.text(`Generated: ${genDate}`, 14, 32);
  let filterText = '';
  if (filters) {
    const parts = [];
    if (filters.propertyId && filters.propertyId !== 'All') {
      const p = demoRepository.getPropertyById(filters.propertyId);
      parts.push(`Property: ${p?.name || filters.propertyId}`);
    }
    if (filters.financialYear && filters.financialYear !== 'All') parts.push(`FY: ${filters.financialYear}`);
    if (filters.transactionType && filters.transactionType !== 'All') parts.push(`Type: ${filters.transactionType}`);
    if (filters.category && filters.category !== 'All') parts.push(`Category: ${filters.category}`);
    if (filters.status && filters.status !== 'All') parts.push(`Status: ${filters.status}`);
    if (filters.includeArchived) parts.push('Include Historical');
    filterText = parts.join(' | ');
  }
  if (filterText) doc.text(`Filters: ${filterText}`, pageW - 14, 32, { align: 'right' });
  doc.setDrawColor(245, 158, 11);
  doc.setLineWidth(0.5);
  doc.line(14, 38, pageW - 14, 38);
};

const addPdfFooter = (doc) => {
  const pageH = doc.internal.pageSize.getHeight();
  const pageW = doc.internal.pageSize.getWidth();
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFillColor(15, 23, 42);
    doc.rect(0, pageH - 18, pageW, 18, 'F');
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(8);
    doc.text("Generated by Malhotra's Property Portfolio", 14, pageH - 7);
    doc.text(`Page ${i} of ${totalPages}`, pageW - 14, pageH - 7, { align: 'right' });
  }
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { showToast, showStagedMessages, showConfirm } = useFeedback();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isHistoricalSummaryOpen, setIsHistoricalSummaryOpen] = useState(false);
  const [isSavingHistoricalSummary, setIsSavingHistoricalSummary] = useState(false);
  const [historicalSummaryForm, setHistoricalSummaryForm] = useState({
    propertyId: '',
    financialYear: HISTORICAL_FY_OPTIONS[0] || '',
    income: '',
    operatingExpenses: '',
    loanInterest: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    propertyId: 'All', spaceId: 'All', tenantId: 'All',
    financialYear: calculations.getAustralianFinancialYear(new Date()),
    startDate: null, endDate: null, transactionType: 'All', category: 'All', status: 'All', includeArchived: false
  });
  const [dashboardPropertyOrder, setDashboardPropertyOrder] = useState(() => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(DASHBOARD_PROPERTY_ORDER_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  });
  const [draggedPropertyId, setDraggedPropertyId] = useState(null);
  const desktopDisplayName = useMemo(() => {
    const user = pocketbaseService.auth?.getState?.()?.user || {};
    const splitName = [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(' ').trim();
    return String(user.name || user.displayName || user.display_name || user.fullName || user.full_name || splitName || 'there').trim() || 'there';
  }, []);
  const selectedFinancialYear = filters.financialYear !== 'All'
    ? filters.financialYear
    : calculations.getAustralianFinancialYear(new Date());
  const previousFinancialYear = getPreviousFinancialYear(selectedFinancialYear);
  const isSelectedCurrentFinancialYear = selectedFinancialYear === calculations.getAustralianFinancialYear(new Date());

  useEffect(() => {
    const handleDataRefreshed = () => setRefreshTrigger(prev => prev + 1);
    window.addEventListener('mpp:data-refreshed', handleDataRefreshed);
    return () => window.removeEventListener('mpp:data-refreshed', handleDataRefreshed);
  }, []);

  const properties = useMemo(() => {
    const all = demoRepository.getAllProperties();
    if (filters.includeArchived) return all;
    return all.filter(p => !['archived', 'sold'].includes(normaliseStatus(p.status)));
  }, [filters.includeArchived, refreshTrigger]);

  const historicalEntryProperties = useMemo(() => {
    return demoRepository.getAllProperties().filter(p => normaliseStatus(p.status) !== 'archived');
  }, [refreshTrigger]);

  const availableFinancialYears = useMemo(() => {
    const years = new Set([
      calculations.getAustralianFinancialYear(new Date()),
      ...HISTORICAL_FY_OPTIONS,
      ...demoRepository.getTransactions().map(getTransactionFinancialYear).filter(Boolean)
    ]);
    return [...years].sort((a, b) => Number(String(b).split('-')[0]) - Number(String(a).split('-')[0]));
  }, [refreshTrigger]);

  const spaces = useMemo(() => {
    if (filters.propertyId === 'All') return demoRepository.getAllSpaces ? demoRepository.getAllSpaces() : [];
    return demoRepository.getSpacesByProperty(filters.propertyId);
  }, [filters.propertyId, refreshTrigger]);

  const leases = useMemo(() => {
    let all = demoRepository.getAllLeases();
    if (filters.propertyId !== 'All') {
      all = all.filter(l => l.propertyId === filters.propertyId);
    }
    return all.filter(l => normaliseStatus(l.status) !== 'archived');
  }, [filters.propertyId, refreshTrigger]);

  const filteredTransactions = useMemo(() => {
    let txns = demoRepository.getTransactions();
    if (filters.propertyId !== 'All') txns = txns.filter(t => t.propertyId === filters.propertyId);
    if (filters.financialYear !== 'All') txns = txns.filter(t => getTransactionFinancialYear(t) === filters.financialYear);
    if (filters.transactionType !== 'All') txns = txns.filter(t => t.type === filters.transactionType);
    if (filters.category !== 'All') txns = txns.filter(t => t.category === filters.category);
    if (filters.status !== 'All') txns = txns.filter(t => normaliseStatus(t.status) === normaliseStatus(filters.status));
    if (!filters.includeArchived) txns = txns.filter(t => normaliseStatus(t.status) !== 'archived');
    return txns;
  }, [filters, refreshTrigger]);

  const scopedPropertyIds = useMemo(() => {
    if (filters.propertyId !== 'All') return [filters.propertyId];
    if (filters.includeArchived) return properties.map(property => property.id);
    return null;
  }, [filters.propertyId, filters.includeArchived, properties]);

  const portfolioSummary = useMemo(() => {
    return calculations.getPortfolioSummary(scopedPropertyIds, selectedFinancialYear);
  }, [scopedPropertyIds, selectedFinancialYear, refreshTrigger]);

  const propertySummaries = useMemo(() => {
    const includedIds = new Set((portfolioSummary.includedPropertyIds || []).map(String));
    return properties
      .filter(prop => includedIds.has(String(prop.id)))
      .map(prop => ({
        property: prop,
        summary: calculations.getPropertySummary(prop.id, selectedFinancialYear),
        previousSummary: previousFinancialYear ? calculations.getPropertySummary(prop.id, previousFinancialYear) : null
      }));
  }, [properties, portfolioSummary.includedPropertyIds, selectedFinancialYear, previousFinancialYear, refreshTrigger]);

  const currentProjectionSummary = useMemo(() => {
    return calculations.getProjectedPortfolioPosition(scopedPropertyIds);
  }, [scopedPropertyIds, refreshTrigger]);

  const orderedPropertySummaries = useMemo(() => {
    const pinned = [];
    const investments = [];
    propertySummaries.forEach((item, index) => {
      const financial = getPropertyFinancialDetails(item.property);
      const entry = { ...item, originalIndex: index };
      if (isOwnerOccupiedProperty(item.property, financial.loanType)) pinned.push(entry);
      else investments.push(entry);
    });
    const orderMap = new Map(dashboardPropertyOrder.map((id, index) => [String(id), index]));
    investments.sort((a, b) => {
      const aRank = orderMap.has(String(a.property.id)) ? orderMap.get(String(a.property.id)) : Number.MAX_SAFE_INTEGER;
      const bRank = orderMap.has(String(b.property.id)) ? orderMap.get(String(b.property.id)) : Number.MAX_SAFE_INTEGER;
      return aRank - bRank || a.originalIndex - b.originalIndex;
    });
    return [...pinned, ...investments].map(({ originalIndex, ...item }) => item);
  }, [propertySummaries, dashboardPropertyOrder]);

  useEffect(() => {
    const investmentIds = properties
      .filter(property => !isOwnerOccupiedProperty(property, getPropertyFinancialDetails(property).loanType))
      .map(property => String(property.id));
    setDashboardPropertyOrder(previous => {
      const previousIds = previous.map(String);
      const next = [
        ...previousIds.filter(id => investmentIds.includes(id)),
        ...investmentIds.filter(id => !previousIds.includes(id))
      ];
      if (next.length === previousIds.length && next.every((id, index) => id === previousIds[index])) return previous;
      try { window.localStorage.setItem(DASHBOARD_PROPERTY_ORDER_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [properties]);

  const persistDashboardPropertyOrder = useCallback((next) => {
    const normalized = next.map(String);
    setDashboardPropertyOrder(normalized);
    try { window.localStorage.setItem(DASHBOARD_PROPERTY_ORDER_KEY, JSON.stringify(normalized)); } catch {}
  }, []);

  const handlePropertyDragStart = useCallback((event, propertyId) => {
    const item = propertySummaries.find(entry => String(entry.property.id) === String(propertyId));
    if (!item || isOwnerOccupiedProperty(item.property, getPropertyFinancialDetails(item.property).loanType)) {
      event.preventDefault();
      return;
    }
    setDraggedPropertyId(String(propertyId));
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(propertyId));
  }, [propertySummaries]);

  const handlePropertyDrop = useCallback((event, targetPropertyId) => {
    event.preventDefault();
    const sourceId = String(draggedPropertyId || event.dataTransfer.getData('text/plain') || '');
    const targetId = String(targetPropertyId || '');
    if (!sourceId || !targetId || sourceId === targetId) {
      setDraggedPropertyId(null);
      return;
    }
    const target = propertySummaries.find(entry => String(entry.property.id) === targetId);
    if (!target || isOwnerOccupiedProperty(target.property, getPropertyFinancialDetails(target.property).loanType)) {
      setDraggedPropertyId(null);
      return;
    }
    const investmentIds = orderedPropertySummaries
      .filter(entry => !isOwnerOccupiedProperty(entry.property, getPropertyFinancialDetails(entry.property).loanType))
      .map(entry => String(entry.property.id));
    const sourceIndex = investmentIds.indexOf(sourceId);
    const targetIndex = investmentIds.indexOf(targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      setDraggedPropertyId(null);
      return;
    }
    const next = [...investmentIds];
    next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, sourceId);
    persistDashboardPropertyOrder(next);
    setDraggedPropertyId(null);
  }, [draggedPropertyId, orderedPropertySummaries, propertySummaries, persistDashboardPropertyOrder]);

  const historicalSummaryTotals = useMemo(() => {
    const totals = { income: 0, operatingExpenses: 0, loanInterest: 0, recordCount: 0 };
    filteredTransactions.forEach(transaction => {
      if (normaliseStatus(transaction.status) !== 'paid') return;
      const marker = calculations.getHistoricalFySummaryMarker(transaction);
      if (!marker) return;
      const amount = Math.abs(Number(transaction.amount) || 0);
      totals.recordCount += 1;
      if (marker === calculations.HISTORICAL_FY_SUMMARY_MARKERS.income) totals.income += amount;
      else if (marker === calculations.HISTORICAL_FY_SUMMARY_MARKERS.operatingExpenses) totals.operatingExpenses += amount;
      else if (marker === calculations.HISTORICAL_FY_SUMMARY_MARKERS.loanInterest) totals.loanInterest += amount;
    });
    return {
      income: roundMoney(totals.income),
      operatingExpenses: roundMoney(totals.operatingExpenses),
      loanInterest: roundMoney(totals.loanInterest),
      recordCount: totals.recordCount,
      hasData: totals.recordCount > 0
    };
  }, [filteredTransactions]);

  const monthlyIncomeVsExpenses = useMemo(() => {
    const months = buildDashboardMonths(filters.financialYear);
    return months.map(m => {
      const monthStart = m.timestamp;
      const monthEndDate = new Date(monthStart);
      monthEndDate.setMonth(monthEndDate.getMonth() + 1);
      const monthEnd = monthEndDate.getTime();
      const monthTxns = filteredTransactions.filter(t => {
        if (calculations.isHistoricalFySummaryTransaction(t)) return false;
        const txnDate = t.date;
        return txnDate >= monthStart && txnDate < monthEnd;
      });
      let income = 0;
      let expenses = 0;
      monthTxns.forEach(t => {
        const classified = calculations.classifyTransactionForPerformance(t);
        if (!classified.realised) return;
        if (classified.classification === 'operating_income') income += classified.amount;
        else if (classified.classification === 'operating_expense') expenses += classified.amount;
      });
      return { month: m.month, income: Math.round(income * 100) / 100, expenses: Math.round(expenses * 100) / 100 };
    });
  }, [filteredTransactions, filters.financialYear]);

  const propertyPerformanceData = useMemo(() => {
    return propertySummaries.map(({ property, summary }) => ({
      name: property.name.length > 20 ? property.name.substring(0, 20) + '...' : property.name,
      netCashFlow: Math.round(summary.netCashFlow * 100) / 100,
      annualRent: Math.round(summary.annualRent * 100) / 100,
      occupancy: Math.round(summary.occupancyRate * 100 * 100) / 100
    }));
  }, [propertySummaries]);

  const outgoingsByCategory = useMemo(() => {
    const categories = {};
    filteredTransactions.forEach(t => {
      const classified = calculations.classifyTransactionForPerformance(t);
      if (!classified.realised || classified.classification !== 'operating_expense') return;
      const category = classified.historicalSummaryMarker === calculations.HISTORICAL_FY_SUMMARY_MARKERS.operatingExpenses
        ? 'Historical FY Aggregate Operating Expenses'
        : humaniseCategory(calculations.normaliseTransactionCategory(t.category) || 'uncategorised');
      if (!categories[category]) { categories[category] = 0; }
      categories[category] += classified.amount;
    });
    return Object.entries(categories)
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value);
  }, [filteredTransactions]);

  const openItemsByProperty = useMemo(() => {
    const allOpenItems = demoRepository.getAllOpenItems().filter(oi => {
      if (filters.propertyId !== 'All' && String(oi.propertyId || '') !== String(filters.propertyId)) return false;
      const status = normaliseStatus(oi.status);
      if (!filters.includeArchived) return status !== 'archived' && status !== 'resolved' && status !== 'closed';
      return true;
    });
    const byProperty = {};
    allOpenItems.forEach(oi => {
      const prop = demoRepository.getPropertyById(oi.propertyId);
      const propName = prop?.name || 'Unknown';
      if (!byProperty[propName]) { byProperty[propName] = { open: 0, inProgress: 0, waiting: 0 }; }
      const status = normaliseStatus(oi.status);
      if (status === 'open') byProperty[propName].open++;
      else if (status === 'in_progress') byProperty[propName].inProgress++;
      else byProperty[propName].waiting++;
    });
    return Object.entries(byProperty).map(([name, counts]) => ({ name, open: counts.open, inProgress: counts.inProgress, waiting: counts.waiting }));
  }, [filters.propertyId, filters.includeArchived, refreshTrigger]);

  const insuranceSummary = useMemo(() => {
    const policies = filters.propertyId !== 'All' ? demoRepository.getInsuranceByProperty(filters.propertyId) : demoRepository.getAllInsurancePolicies();
    const active = policies.filter(p => normaliseStatus(p.status) === 'active');
    const expiringSoon = active.filter(p => { const days = calculations.calculateDaysToExpiry(p.expiryDate); return days > 0 && days <= 30; });
    const expired = active.filter(p => calculations.calculateDaysToExpiry(p.expiryDate) < 0);
    return { total: policies.length, active: active.length, expiringSoon: expiringSoon.length, expired: expired.length, totalPremium: Math.round(active.reduce((sum, p) => sum + p.premium, 0) * 100) / 100 };
  }, [filters.propertyId, refreshTrigger]);

  const mobileUpcomingDates = useMemo(() => {
    const events = [];
    const inScope = (propertyId) => filters.propertyId === 'All' || String(propertyId || '') === String(filters.propertyId);

    demoRepository.getAllLeases().forEach((lease) => {
      if (!inScope(lease.propertyId) || normaliseStatus(lease.status) !== 'active') return;
      const endDate = lease.endDate ?? lease.leaseEnd;
      if (!endDate) return;
      const days = calculations.calculateDaysToExpiry(endDate);
      if (!Number.isFinite(days) || days < 0) return;
      const property = demoRepository.getPropertyById(lease.propertyId);
      const space = lease.spaceId ? demoRepository.getSpaceById(lease.spaceId) : null;
      const tenant = lease.tenantId ? demoRepository.getTenantById(lease.tenantId) : null;
      const tenantName = [tenant?.firstName, tenant?.lastName].filter(Boolean).join(' ').trim();
      events.push({
        id: `lease-${lease.id}`,
        type: 'lease',
        label: 'Lease expiry',
        propertyName: property?.name || 'Property',
        contextPrimary: space?.name || space?.spaceName || 'Whole property',
        contextSecondary: tenantName || 'Tenant not linked',
        date: endDate,
        days,
        route: lease.tenantId ? `/tenants/${lease.tenantId}` : '/important-dates'
      });
    });

    demoRepository.getAllInsurancePolicies().forEach((policy) => {
      if (!inScope(policy.propertyId) || !['active', 'renewal_due', 'claim_open'].includes(normaliseStatus(policy.status))) return;
      if (!policy.expiryDate) return;
      const days = calculations.calculateDaysToExpiry(policy.expiryDate);
      if (!Number.isFinite(days) || days < 0) return;
      const property = demoRepository.getPropertyById(policy.propertyId);
      const linkedSpace = policy.spaceId ? demoRepository.getSpaceById(policy.spaceId) : null;
      const unitLabel = String(
        linkedSpace?.name
        || linkedSpace?.spaceName
        || policy.unitName
        || policy.unit
        || policy.dwelling
        || policy.appliesTo
        || policy.metadata?.unitName
        || policy.metadata?.unit
        || policy.metadata?.dwelling
        || policy.metadata?.appliesTo
        || ''
      ).trim();
      const provider = String(policy.provider || '').trim();
      const policyType = humaniseCategory(policy.policyType || 'insurance');
      const policyNumber = String(policy.policyNumber || '').trim();
      events.push({
        id: `insurance-${policy.id}`,
        type: 'insurance',
        label: 'Insurance renewal',
        propertyName: property?.name || 'Property',
        contextPrimary: unitLabel || provider || policyType,
        contextSecondary: unitLabel
          ? [provider, policyType].filter(Boolean).join(' · ')
          : [policyType, policyNumber ? `Policy ${policyNumber}` : ''].filter(Boolean).join(' · '),
        date: policy.expiryDate,
        days,
        route: `/insurance?policy=${encodeURIComponent(policy.id)}`
      });
    });

    return events.sort((a, b) => a.days - b.days || String(a.propertyName).localeCompare(String(b.propertyName), 'en-AU')).slice(0, 6);
  }, [filters.propertyId, refreshTrigger]);

  const mobileOpenItemsCount = useMemo(
    () => openItemsByProperty.reduce((sum, item) => sum + Number(item.open || 0) + Number(item.inProgress || 0) + Number(item.waiting || 0), 0),
    [openItemsByProperty]
  );

  const mobileInsuranceRenewalCount = useMemo(() => {
    const policies = filters.propertyId !== 'All'
      ? demoRepository.getInsuranceByProperty(filters.propertyId)
      : demoRepository.getAllInsurancePolicies();
    return policies.filter(policy => {
      if (!['active', 'renewal_due', 'claim_open'].includes(normaliseStatus(policy.status)) || !policy.expiryDate) return false;
      const days = calculations.calculateDaysToExpiry(policy.expiryDate);
      return Number.isFinite(days) && days >= 0 && days <= 90;
    }).length;
  }, [filters.propertyId, refreshTrigger]);

  const mobileDateLabel = useMemo(
    () => new Date().toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
    []
  );

  const mobileGreeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const documentSummary = useMemo(() => {
    const docs = filters.propertyId !== 'All' ? demoRepository.getDocumentsByProperty(filters.propertyId) : demoRepository.getDocuments();
    const byType = {};
    const activeDocs = docs.filter(d => normaliseStatus(d.status) === 'active');
    activeDocs.forEach(d => { byType[d.documentType] = (byType[d.documentType] || 0) + 1; });
    return { total: docs.length, active: activeDocs.length, byType };
  }, [filters.propertyId, refreshTrigger]);

  const inspectionSummary = useMemo(() => {
    const inspections = filters.propertyId !== 'All' ? demoRepository.getInspectionsByProperty(filters.propertyId) : demoRepository.getInspections();
    return { total: inspections.length, completed: inspections.filter(i => normaliseStatus(i.status) === 'completed').length, scheduled: inspections.filter(i => normaliseStatus(i.status) === 'scheduled').length, withIssues: inspections.filter(i => i.issues?.length > 0).length };
  }, [filters.propertyId, refreshTrigger]);

  const showHistoricalAggregateChart = filters.propertyId !== 'All'
    && filters.financialYear !== 'All'
    && historicalSummaryTotals.hasData;
  const monthlyChartHasData = monthlyIncomeVsExpenses.some(item => Math.abs(item.income) > 0 || Math.abs(item.expenses) > 0);
  const propertyPerformanceHasData = propertyPerformanceData.some(item => Number.isFinite(item.netCashFlow) && Math.abs(item.netCashFlow) > 0);
  const outgoingsChartHasData = outgoingsByCategory.length > 0;
  const openItemsChartHasData = openItemsByProperty.length > 0;
  const incomeVsExpensesHasData = showHistoricalAggregateChart || monthlyChartHasData;
  const portfolioAnalysisDataCount = [incomeVsExpensesHasData, propertyPerformanceHasData, outgoingsChartHasData, openItemsChartHasData].filter(Boolean).length;
  const hasAnyPortfolioAnalysisData = portfolioAnalysisDataCount > 0;
  const portfolioAnalysisScopeKey = [
    filters.propertyId,
    filters.financialYear,
    filters.transactionType,
    filters.category,
    filters.status,
    filters.includeArchived ? 'historical' : 'current',
    filters.startDate || '',
    filters.endDate || ''
  ].join('|');
  const [portfolioAnalysisDisclosure, setPortfolioAnalysisDisclosure] = useState({
    scopeKey: '',
    open: false,
    manual: false
  });
  const isPortfolioAnalysisOpen = portfolioAnalysisDisclosure.scopeKey === portfolioAnalysisScopeKey
    ? portfolioAnalysisDisclosure.open
    : hasAnyPortfolioAnalysisData;
  const portfolioAnalysisStatusText = hasAnyPortfolioAnalysisData
    ? `${portfolioAnalysisDataCount} of 4 charts have data`
    : 'No chart data for the current selection';

  useEffect(() => {
    setPortfolioAnalysisDisclosure(previous => {
      if (previous.scopeKey !== portfolioAnalysisScopeKey) {
        return {
          scopeKey: portfolioAnalysisScopeKey,
          open: hasAnyPortfolioAnalysisData,
          manual: false
        };
      }
      if (!previous.manual && previous.open !== hasAnyPortfolioAnalysisData) {
        return {
          ...previous,
          open: hasAnyPortfolioAnalysisData
        };
      }
      return previous;
    });
  }, [portfolioAnalysisScopeKey, hasAnyPortfolioAnalysisData]);

  const togglePortfolioAnalysis = useCallback(() => {
    setPortfolioAnalysisDisclosure({
      scopeKey: portfolioAnalysisScopeKey,
      open: !isPortfolioAnalysisOpen,
      manual: true
    });
  }, [portfolioAnalysisScopeKey, isPortfolioAnalysisOpen]);

  const incomeVsExpensesChartOption = useMemo(() => {
    if (showHistoricalAggregateChart) {
      return {
        title: { text: `Historical FY Aggregate · FY ${selectedFinancialYear}`, left: 'center', textStyle: { fontSize: 14, fontWeight: '600' } },
        tooltip: { trigger: 'axis', formatter: (params) => params.map(p => `${p.seriesName}: $${Number(p.value || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`).join('<br/>') },
        legend: { data: ['Operating Income', 'Operating Expenses', 'Loan Interest'], bottom: 0 },
        grid: { left: '3%', right: '4%', bottom: '10%', top: '18%', containLabel: true },
        xAxis: { type: 'category', data: [`FY ${selectedFinancialYear}`] },
        yAxis: { type: 'value', axisLabel: { formatter: (value) => `$${(value / 1000).toFixed(0)}k` } },
        series: [
          { name: 'Operating Income', type: 'bar', data: [historicalSummaryTotals.income] },
          { name: 'Operating Expenses', type: 'bar', data: [historicalSummaryTotals.operatingExpenses] },
          { name: 'Loan Interest', type: 'bar', data: [historicalSummaryTotals.loanInterest] }
        ]
      };
    }

    return {
      title: { text: filters.financialYear !== 'All' ? `Monthly Operating Income vs Expenses · FY ${selectedFinancialYear}` : 'Monthly Operating Income vs Expenses · Last 12 Months', left: 'center', textStyle: { fontSize: 14, fontWeight: '600' } },
      tooltip: { trigger: 'axis', formatter: (params) => { const month = params[0].name; const income = params.find(p => p.seriesName === 'Income')?.value || 0; const expenses = params.find(p => p.seriesName === 'Expenses')?.value || 0; return `${month}<br/>Income: $${income.toLocaleString('en-AU', { minimumFractionDigits: 2 })}<br/>Expenses: $${expenses.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`; } },
      legend: { data: ['Income', 'Expenses'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '10%', top: '15%', containLabel: true },
      xAxis: { type: 'category', data: monthlyIncomeVsExpenses.map(d => d.month), axisLabel: { rotate: 45, fontSize: 10 } },
      yAxis: { type: 'value', axisLabel: { formatter: (value) => `$${(value / 1000).toFixed(0)}k` } },
      series: [
        { name: 'Income', type: 'bar', data: monthlyIncomeVsExpenses.map(d => d.income) },
        { name: 'Expenses', type: 'bar', data: monthlyIncomeVsExpenses.map(d => d.expenses) }
      ]
    };
  }, [showHistoricalAggregateChart, historicalSummaryTotals, monthlyIncomeVsExpenses, filters.financialYear, selectedFinancialYear]);

  const propertyPerformanceChartOption = useMemo(() => ({
    title: { text: `Property Cash Flow After Finance · FY ${selectedFinancialYear}`, left: 'center', textStyle: { fontSize: 14, fontWeight: '600' } },
    tooltip: { formatter: (params) => `${params.name}<br/>Cash Flow After Finance: $${params.value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}` },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '15%', containLabel: true },
    xAxis: { type: 'category', data: propertyPerformanceData.map(d => d.name), axisLabel: { rotate: 45, fontSize: 10 } },
    yAxis: { type: 'value', axisLabel: { formatter: (value) => `$${(value / 1000).toFixed(0)}k` } },
    series: [
      { type: 'bar', data: propertyPerformanceData.map(d => d.netCashFlow) }
    ]
  }), [propertyPerformanceData, selectedFinancialYear]);

  const outgoingsChartOption = useMemo(() => ({
    title: { text: 'Operating Expenses by Category', left: 'center', textStyle: { fontSize: 14, fontWeight: '600' } },
    tooltip: { formatter: (params) => `${params.name}<br/>$${params.value.toLocaleString('en-AU', { minimumFractionDigits: 2 })} (${params.percent}%)` },
    series: [
      { type: 'pie', radius: ['40%', '70%'], avoidLabelOverlap: false, itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 }, label: { formatter: '{b}\n${c}' }, data: outgoingsByCategory }
    ]
  }), [outgoingsByCategory]);

  const openItemsChartOption = useMemo(() => ({
    title: { text: 'Open Items by Property', left: 'center', textStyle: { fontSize: 14, fontWeight: '600' } },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: ['Open', 'In Progress', 'Waiting'], bottom: 0 },
    grid: { left: '3%', right: '4%', bottom: '10%', top: '15%', containLabel: true },
    xAxis: { type: 'category', data: openItemsByProperty.map(d => d.name), axisLabel: { rotate: 45, fontSize: 10 } },
    yAxis: { type: 'value' },
    series: [
      { name: 'Open', type: 'bar', stack: 'total', data: openItemsByProperty.map(d => d.open) },
      { name: 'In Progress', type: 'bar', stack: 'total', data: openItemsByProperty.map(d => d.inProgress) },
      { name: 'Waiting', type: 'bar', stack: 'total', data: openItemsByProperty.map(d => d.waiting) }
    ]
  }), [openItemsByProperty]);

  const openHistoricalSummaryModal = useCallback(() => {
    const currentFy = calculations.getAustralianFinancialYear(new Date());
    setHistoricalSummaryForm(prev => ({
      ...prev,
      propertyId: filters.propertyId !== 'All' ? filters.propertyId : prev.propertyId,
      financialYear: filters.financialYear !== 'All' && filters.financialYear !== currentFy && HISTORICAL_FY_OPTIONS.includes(filters.financialYear)
        ? filters.financialYear
        : (prev.financialYear || HISTORICAL_FY_OPTIONS[0] || '')
    }));
    setIsHistoricalSummaryOpen(true);
  }, [filters.propertyId, filters.financialYear]);

  const persistHistoricalSummary = useCallback(async (summary) => {
    const existingSummaries = demoRepository.getTransactions().filter(transaction => (
      String(transaction.propertyId || '') === String(summary.propertyId)
      && getTransactionFinancialYear(transaction) === summary.financialYear
      && normaliseStatus(transaction.status) !== 'archived'
      && calculations.isHistoricalFySummaryTransaction(transaction)
    ));

    if (existingSummaries.length > 0) {
      showToast(`A Historical FY Summary already exists for this property and FY ${summary.financialYear}. Archive or review the existing summary before creating another.`, 'error');
      return;
    }

    const transactionDate = getHistoricalFyStartTimestamp(summary.financialYear);
    if (!transactionDate) {
      showToast('The selected financial year is invalid.', 'error');
      return;
    }

    const baseNotes = [
      'Historical annual aggregate entered from agent/lender financial statements.',
      'This is not an itemised transaction record.',
      summary.notes ? `Source note: ${summary.notes}` : ''
    ].filter(Boolean).join(' ');

    const rows = [
      summary.income > 0 ? {
        propertyId: summary.propertyId,
        date: transactionDate,
        type: 'income',
        category: 'rent',
        amount: Math.abs(summary.income),
        status: 'paid',
        description: `Historical FY Summary — total rent / operating income received (${summary.financialYear})`,
        notes: baseNotes,
        taxCategory: 'accountant_review',
        financialYear: summary.financialYear,
        month: calculations.HISTORICAL_FY_SUMMARY_MARKERS.income
      } : null,
      summary.operatingExpenses > 0 ? {
        propertyId: summary.propertyId,
        date: transactionDate,
        type: 'expense',
        category: 'other_expense',
        amount: -Math.abs(summary.operatingExpenses),
        status: 'paid',
        description: `Historical FY Summary — total property operating expenses paid (${summary.financialYear})`,
        notes: `${baseNotes} Excludes loan interest, principal repayments, capital expenditure and owner transfers/disbursements.`,
        taxCategory: 'accountant_review',
        financialYear: summary.financialYear,
        month: calculations.HISTORICAL_FY_SUMMARY_MARKERS.operatingExpenses
      } : null,
      summary.loanInterest > 0 ? {
        propertyId: summary.propertyId,
        date: transactionDate,
        type: 'loan',
        category: 'loan_interest',
        amount: -Math.abs(summary.loanInterest),
        status: 'paid',
        description: `Historical FY Summary — total loan interest paid (${summary.financialYear})`,
        notes: `${baseNotes} Recorded separately from property operating expenses and excluded from NOI.`,
        taxCategory: 'accountant_review',
        financialYear: summary.financialYear,
        month: calculations.HISTORICAL_FY_SUMMARY_MARKERS.loanInterest
      } : null
    ].filter(Boolean);

    if (rows.length === 0) {
      showToast('Enter at least one historical amount greater than $0.00.', 'error');
      return;
    }

    setIsSavingHistoricalSummary(true);
    const savedRecords = [];
    try {
      for (const row of rows) {
        const result = await demoRepository.addTransaction(row);
        if (!result?.success) {
          throw new Error(result?.error || `Failed to save ${row.description}`);
        }
        if (result.record) savedRecords.push(result.record);
      }

      setIsHistoricalSummaryOpen(false);
      setHistoricalSummaryForm({
        propertyId: '',
        financialYear: HISTORICAL_FY_OPTIONS[0] || '',
        income: '',
        operatingExpenses: '',
        loanInterest: '',
        notes: ''
      });
      setFilters(prev => ({
        ...prev,
        propertyId: summary.propertyId,
        financialYear: summary.financialYear,
        transactionType: 'All',
        category: 'All',
        status: 'All'
      }));
      setRefreshTrigger(prev => prev + 1);
      showToast(`Historical FY ${summary.financialYear} summary saved successfully as ${rows.length} auditable aggregate ledger record(s).`, 'success');
    } catch (error) {
      let rollbackFailed = false;
      for (const record of savedRecords) {
        try {
          const rollbackResult = await demoRepository.updateTransaction(record.id, {
            status: 'archived',
            notes: `${record.notes || ''} Automatically archived because the Historical FY Summary save did not complete.`.trim()
          });
          if (!rollbackResult?.success) rollbackFailed = true;
        } catch {
          rollbackFailed = true;
        }
      }
      showToast(
        rollbackFailed
          ? `Historical FY Summary did not complete and one or more partial records could not be rolled back automatically. Review the Transactions Ledger before retrying. ${error?.message || ''}`.trim()
          : `Historical FY Summary was not saved. Any partial records were archived automatically. ${error?.message || ''}`.trim(),
        'error'
      );
    } finally {
      setIsSavingHistoricalSummary(false);
    }
  }, [showToast]);

  const handleHistoricalSummarySubmit = useCallback((event) => {
    event.preventDefault();
    const income = Number(historicalSummaryForm.income || 0);
    const operatingExpenses = Number(historicalSummaryForm.operatingExpenses || 0);
    const loanInterest = Number(historicalSummaryForm.loanInterest || 0);
    const currentFy = calculations.getAustralianFinancialYear(new Date());

    if (!historicalSummaryForm.propertyId) {
      showToast('Select the property for this historical financial-year summary.', 'error');
      return;
    }
    if (!historicalSummaryForm.financialYear || historicalSummaryForm.financialYear === currentFy || !HISTORICAL_FY_OPTIONS.includes(historicalSummaryForm.financialYear)) {
      showToast('Historical FY Summary is restricted to completed previous financial years.', 'error');
      return;
    }
    if ([income, operatingExpenses, loanInterest].some(value => !Number.isFinite(value) || value < 0)) {
      showToast('Historical amounts must be valid positive numbers or zero.', 'error');
      return;
    }
    if (income <= 0 && operatingExpenses <= 0 && loanInterest <= 0) {
      showToast('Enter at least one historical amount greater than $0.00.', 'error');
      return;
    }

    const summary = {
      propertyId: historicalSummaryForm.propertyId,
      financialYear: historicalSummaryForm.financialYear,
      income: roundMoney(income),
      operatingExpenses: roundMoney(operatingExpenses),
      loanInterest: roundMoney(loanInterest),
      notes: String(historicalSummaryForm.notes || '').trim()
    };

    const overlappingDetailedRecords = demoRepository.getTransactions().filter(transaction => {
      if (String(transaction.propertyId || '') !== String(summary.propertyId)) return false;
      if (getTransactionFinancialYear(transaction) !== summary.financialYear) return false;
      if (normaliseStatus(transaction.status) === 'archived') return false;
      if (calculations.isHistoricalFySummaryTransaction(transaction)) return false;
      if (normaliseStatus(transaction.status) !== 'paid') return false;
      const classified = calculations.classifyTransactionForPerformance(transaction);
      const category = calculations.normaliseTransactionCategory(transaction.category);
      return classified.classification === 'operating_income'
        || classified.classification === 'operating_expense'
        || category === 'loan_interest';
    });

    if (overlappingDetailedRecords.length > 0) {
      showConfirm({
        title: 'Possible Double Counting',
        message: `${overlappingDetailedRecords.length} Paid detailed income/operating-expense/loan-interest record(s) already exist for this property in FY ${summary.financialYear}. Adding a full-year aggregate on top of them may double-count that year. Only continue if the annual statement totals do not overlap those records.`,
        confirmText: 'Continue Carefully',
        isDestructive: false,
        onConfirm: async () => persistHistoricalSummary(summary)
      });
      return;
    }

    persistHistoricalSummary(summary);
  }, [historicalSummaryForm, persistHistoricalSummary, showConfirm, showToast]);

  const handleExportCSV = useCallback(() => {
    showStagedMessages([
      'Preparing report data...', 'Calculating totals...', 'Formatting export...', 'Export ready!'
    ], () => {
      const headers = ['Date', 'Type', 'Category', 'Property', 'Description', 'Amount', 'Status', 'FY'];
      const rows = filteredTransactions.map(t => {
        const prop = demoRepository.getPropertyById(t.propertyId);
        return [
          calculations.formatDateAU(t.date), t.type, t.category, prop?.name || '-', t.description, t.amount.toFixed(2), t.status, getTransactionFinancialYear(t)
        ].map(v => `"${v}"`).join(',');
      });
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('CSV export downloaded', 'success');
    });
  }, [filteredTransactions, showStagedMessages, showToast]);

  const handleDownloadPDF = useCallback(() => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      addPdfHeader(doc, 'Portfolio Dashboard Report', filters);

      let y = 44;
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Executive Summary', 14, y);
      y += 6;

      const kpis = [
        ['Portfolio Market Value', formatPdfCurrency(portfolioSummary.totalMarketValue)],
        ['Total Equity', formatPdfCurrency(portfolioSummary.totalEquity)],
        ['Annual Gross Rent (Expected)', formatPdfCurrency(portfolioSummary.annualRent)],
        [`Net Operating Income (FY ${portfolioSummary.currentFinancialYear})`, formatPdfCurrency(portfolioSummary.netOperatingIncome)],
        [`Cash Flow After Finance (FY ${portfolioSummary.currentFinancialYear})`, formatPdfCurrency(portfolioSummary.cashFlowAfterFinance)],
        [portfolioSummary.netYieldComplete ? 'Net Yield (TTM)' : 'Net Yield (TTM · Provisional)', formatPdfPercent(portfolioSummary.netYield) + '%'],
        ['Occupancy Rate', formatPdfPercent(portfolioSummary.occupancyRate) + '%'],
        ['Number of Properties', String(portfolioSummary.totalProperties)]
      ];

      autoTable(doc, {
        startY: y,
        head: [['Metric', 'Value']],
        body: kpis,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [15, 23, 42], textColor: [245, 158, 11] },
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 60, halign: 'right' } }
      });

      y = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Property Breakdown', 14, y);
      y += 6;

      const propRows = propertySummaries.map(({ property: p, summary: s }) => [
        safeText(p.name),
        safeText(p.propertyType),
        formatPdfCurrency(p.currentMarketValue),
        formatPdfCurrency(s.totalAcquisitionCost),
        formatPdfCurrency(s.totalLoanBalance),
        formatPdfCurrency(s.equity),
        formatPdfPercent(s.lvr) + '%',
        formatPdfPercent(s.occupancyRate) + '%',
        formatPdfCurrency(s.netCashFlow)
      ]);

      autoTable(doc, {
        startY: y,
        head: [['Property', 'Type', 'Market Value', 'Acquisition', 'Loan Bal', 'Equity', 'LVR', 'Occupancy', 'Cash Flow After Finance']],
        body: propRows,
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [15, 23, 42], textColor: [245, 158, 11] },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25, halign: 'right' },
          3: { cellWidth: 25, halign: 'right' },
          4: { cellWidth: 25, halign: 'right' },
          5: { cellWidth: 25, halign: 'right' },
          6: { cellWidth: 18, halign: 'right' },
          7: { cellWidth: 18, halign: 'right' },
          8: { cellWidth: 25, halign: 'right' }
        }
      });

      y = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Transaction Summary', 14, y);
      y += 6;

      const txnRows = filteredTransactions.slice(0, 100).map(t => {
        const prop = demoRepository.getPropertyById(t.propertyId);
        return [
          formatPdfDate(t.date),
          safeText(t.type),
          safeText(t.category),
          safeText(prop?.name),
          safeText(t.description),
          formatPdfCurrency(t.amount),
          safeText(t.status),
          safeText(getTransactionFinancialYear(t))
        ];
      });

      const noteText = filteredTransactions.length > 100 ? `Showing first 100 of ${filteredTransactions.length} filtered transactions.` : '';

      autoTable(doc, {
        startY: y,
        head: [['Date', 'Type', 'Category', 'Property', 'Description', 'Amount', 'Status', 'FY']],
        body: txnRows,
        theme: 'striped',
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [15, 23, 42], textColor: [245, 158, 11] },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 18 },
          2: { cellWidth: 25 },
          3: { cellWidth: 30 },
          4: { cellWidth: 50 },
          5: { cellWidth: 22, halign: 'right' },
          6: { cellWidth: 20 },
          7: { cellWidth: 18 }
        }
      });

      if (noteText) {
        y = doc.lastAutoTable.finalY + 4;
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(noteText, 14, y);
      }

      if (portfolioSummary.dataQualityIssues?.length > 0) {
        y = doc.lastAutoTable.finalY + 10;
        if (y > 180) {
          doc.addPage();
          y = 44;
        }
        doc.setTextColor(30, 30, 30);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Calculation Review Notes', 14, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        portfolioSummary.dataQualityIssues.slice(0, 6).forEach((issue, index) => {
          doc.text(`• ${issue}`, 14, y + 6 + (index * 5), { maxWidth: 250 });
        });
      }

      addPdfFooter(doc);
      const filename = `mpp-dashboard-report-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      showToast('Dashboard PDF downloaded', 'success');
    } catch (error) {
      showToast(`PDF export failed: ${error?.message || 'Unknown error'}`, 'error');
    }
  }, [filters, portfolioSummary, propertySummaries, filteredTransactions, showToast]);

  const uniqueCategories = useMemo(() => {
    return [...new Set(demoRepository.getTransactions().map(t => t.category))].filter(Boolean);
  }, [refreshTrigger]);

  const desktopHeroEntry = orderedPropertySummaries.find(item => getPropertyImageUrl(item.property)) || orderedPropertySummaries[0] || null;
  const desktopHeroImage = desktopHeroEntry ? getPropertyImageUrl(desktopHeroEntry.property) : '';
  const desktopStateCount = new Set(orderedPropertySummaries.map(item => item.property?.state).filter(Boolean)).size;
  const desktopFirstName = desktopDisplayName === 'there' ? '' : desktopDisplayName.split(/\s+/).filter(Boolean)[0] || '';
  const desktopScopeLabel = filters.propertyId !== 'All'
    ? demoRepository.getPropertyById(filters.propertyId)?.name || 'Selected property'
    : 'Entire portfolio';

  return (
    <div className="animate-fade-in print:hidden">
      <div className="space-y-4 xl:hidden">
        <section className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-white to-amber-50/70 p-4 shadow-sm sm:p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-xs font-medium text-slate-500">{mobileGreeting}</div>
              <h1 className="mt-1 text-xl font-bold tracking-tight text-slate-950 sm:text-2xl">Your portfolio at a glance</h1>
              <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                {portfolioSummary.totalProperties} {portfolioSummary.totalProperties === 1 ? 'property' : 'properties'} · {calculations.formatPercentage(portfolioSummary.occupancyRate || 0)} occupancy · FY {selectedFinancialYear}
              </p>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Today</div>
              <div className="mt-1 text-xs font-medium text-slate-600">{mobileDateLabel}</div>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-amber-100 bg-white/90 p-4 shadow-[0_8px_28px_rgba(15,23,42,0.05)] sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                <SafeIcon icon="TrendingUp" className="text-xl" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-500">Portfolio Market Value</div>
                <div className="mt-1 whitespace-nowrap text-[clamp(1.75rem,8vw,2.25rem)] font-bold leading-none tracking-tight text-slate-950">{formatDashboardCurrency(portfolioSummary.totalMarketValue)}</div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 sm:text-xs">
                  <span>Acquisition {formatDashboardCurrency(portfolioSummary.totalAcquisitionCost)}</span>
                  <span>Equity {formatDashboardCurrency(portfolioSummary.totalEquity)}</span>
                  <span>LVR {calculations.formatPercentage(portfolioSummary.portfolioLVR || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <MobileDashboardMetric icon="Home" label="Equity" value={formatDashboardCurrency(portfolioSummary.totalEquity)} supportingText={`LVR ${calculations.formatPercentage(portfolioSummary.portfolioLVR || 0)}`} tone="blue" />
          <MobileDashboardMetric icon="Users" label="Occupancy" value={calculations.formatPercentage(portfolioSummary.occupancyRate || 0)} supportingText={`${portfolioSummary.occupiedSpaces} of ${portfolioSummary.totalSpaces} spaces`} tone="emerald" />
          <MobileDashboardMetric icon="DollarSign" label="Annual Gross Rent" value={formatDashboardCurrency(portfolioSummary.annualRent)} supportingText="Expected contractual rent" tone="amber" />
          <MobileDashboardMetric icon="TrendingUp" label="Cash Flow After Finance" value={formatSignedDashboardCurrency(portfolioSummary.cashFlowAfterFinance)} supportingText={`FY ${portfolioSummary.currentFinancialYear}`} tone={portfolioSummary.cashFlowAfterFinance >= 0 ? 'emerald' : 'violet'} />
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-3">
            <h2 className="text-base font-bold text-slate-900">Current Projected Position</h2>
            <p className="mt-0.5 text-[11px] leading-4 text-slate-500">Current rents and current recorded loan rates/terms. Property filter changes this scope; FY filters continue to control recorded historical performance.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-6">
            <MobileDashboardMetric icon="Home" label="PPR Installment" value={currentProjectionSummary.pprProjectionComplete ? formatDashboardCurrency(currentProjectionSummary.pprMonthlyInstallment) : 'Needs data'} supportingText={currentProjectionSummary.pprProjectionComplete ? `${formatDashboardCurrency(currentProjectionSummary.pprAnnualInstallment)} / year` : `${currentProjectionSummary.incompletePprLoanProperties} PPR propert${currentProjectionSummary.incompletePprLoanProperties===1 ? 'y' : 'ies'} missing complete loan inputs`} tone={currentProjectionSummary.pprProjectionComplete ? 'blue' : 'amber'} />
            <MobileDashboardMetric icon="CreditCard" label="Investment Installments" value={currentProjectionSummary.incompleteInvestmentLoanProperties===0 ? formatDashboardCurrency(currentProjectionSummary.investmentMonthlyInstallment) : 'Needs data'} supportingText={currentProjectionSummary.incompleteInvestmentLoanProperties===0 ? `${formatDashboardCurrency(currentProjectionSummary.investmentAnnualInstallment)} / year` : `${currentProjectionSummary.incompleteInvestmentLoanProperties} investment propert${currentProjectionSummary.incompleteInvestmentLoanProperties===1 ? 'y' : 'ies'} missing complete loan inputs`} tone="amber" />
            <MobileDashboardMetric icon="BarChart2" label="Anticipated Gross Rent" value={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? formatDashboardCurrency(currentProjectionSummary.anticipatedMonthlyGrossRent) : 'Needs data'} supportingText={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? `${formatDashboardCurrency(currentProjectionSummary.anticipatedAnnualGrossRent)} / year · before recorded deductions` : `${currentProjectionSummary.incompleteInvestmentRentProperties} investment propert${currentProjectionSummary.incompleteInvestmentRentProperties===1 ? 'y' : 'ies'} missing complete rent inputs`} tone={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? 'blue' : 'amber'} />
            <MobileDashboardMetric icon="DollarSign" label="Anticipated Net Rent" value={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? formatDashboardCurrency(currentProjectionSummary.anticipatedMonthlyNetRent) : 'Needs data'} supportingText={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? `${formatDashboardCurrency(currentProjectionSummary.anticipatedAnnualNetRent)} / year` : 'Complete current rent data is required for every investment property in scope'} tone={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? 'emerald' : 'amber'} />
            <MobileDashboardMetric icon="TrendingUp" label="Projected Investment Net Position" value={currentProjectionSummary.investmentProjectionComplete ? formatSignedDashboardCurrency(currentProjectionSummary.projectedInvestmentMonthlyPosition) : 'Needs data'} supportingText={currentProjectionSummary.investmentProjectionComplete ? `${formatDashboardCurrency(currentProjectionSummary.anticipatedMonthlyNetRent)} net rent - ${formatDashboardCurrency(currentProjectionSummary.investmentMonthlyInstallment)} instalments · ${formatSignedDashboardCurrency(currentProjectionSummary.projectedInvestmentAnnualPosition)} / year` : `${currentProjectionSummary.incompleteInvestmentProperties} investment propert${currentProjectionSummary.incompleteInvestmentProperties===1 ? 'y' : 'ies'} incomplete`} tone={!currentProjectionSummary.investmentProjectionComplete ? 'amber' : currentProjectionSummary.projectedInvestmentMonthlyPosition >= 0 ? 'emerald' : 'red'} />
            <MobileDashboardMetric icon="Activity" label="Investment Property Mix" value={currentProjectionSummary.investmentPropertyCount ? `${currentProjectionSummary.positiveProperties} positive` : 'No investments'} supportingText={currentProjectionSummary.investmentPropertyCount ? `${currentProjectionSummary.negativeProperties} negative · ${currentProjectionSummary.neutralProperties} neutral${currentProjectionSummary.incompleteInvestmentProperties ? ` · ${currentProjectionSummary.incompleteInvestmentProperties} incomplete` : ''}` : 'No investment properties in this scope'} tone={currentProjectionSummary.negativeProperties > 0 ? 'amber' : 'slate'} />
          </div>
          <p className="mt-3 rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2 text-[10px] leading-4 text-amber-900"><strong>Projected only:</strong> investment net position = anticipated monthly net rent less current investment-loan instalments. Instalments use the currently recorded loan balances, interest rates, repayment types and remaining terms; interest-rate or loan changes can change the projection. Anticipated net rent uses current contractual rent and recorded management or rent-schedule deductions; rent increases, vacancies and additional expenses can change it. PPR instalments are shown separately and are excluded from the investment net position. Unrecorded council rates, water, strata, insurance, maintenance and other ownership costs are excluded. Recorded FY cash flow remains the historical performance measure.</p>
        </section>

        <section className="grid grid-cols-3 gap-2 sm:gap-3">
          <button type="button" onClick={() => navigate('/properties')} className="rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all hover:shadow-md">
            <SafeIcon icon="Home" className="text-lg text-slate-500" />
            <div className="mt-2 text-lg font-bold text-slate-900">{portfolioSummary.totalProperties}</div>
            <div className="text-[10px] font-medium text-slate-500 sm:text-xs">Properties</div>
          </button>
          <button type="button" onClick={() => navigate('/open-items')} className="rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all hover:shadow-md">
            <SafeIcon icon="AlertCircle" className="text-lg text-red-500" />
            <div className="mt-2 text-lg font-bold text-slate-900">{mobileOpenItemsCount}</div>
            <div className="text-[10px] font-medium text-slate-500 sm:text-xs">Open Items</div>
          </button>
          <button type="button" onClick={() => navigate('/important-dates')} className="rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all hover:shadow-md">
            <SafeIcon icon="Shield" className="text-lg text-amber-600" />
            <div className="mt-2 text-lg font-bold text-slate-900">{mobileInsuranceRenewalCount}</div>
            <div className="text-[10px] font-medium text-slate-500 sm:text-xs">Insurance · 90d</div>
          </button>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><SafeIcon icon="Calendar" /></div>
              <div>
                <h2 className="text-base font-bold text-slate-900">Important Dates</h2>
                <p className="text-[11px] text-slate-500">Nearest lease and insurance dates</p>
              </div>
            </div>
            <button type="button" onClick={() => navigate('/important-dates')} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">View All <SafeIcon icon="ArrowRight" /></button>
          </div>
          {mobileUpcomingDates.length > 0 ? (
            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {mobileUpcomingDates.slice(0, 4).map(event => (
                <MobileCountdownCard key={event.id} event={event} onOpen={() => navigate(event.route)} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-7 text-center">
              <SafeIcon icon="Calendar" className="mx-auto text-2xl text-slate-300" />
              <div className="mt-2 text-sm font-semibold text-slate-700">No upcoming lease or insurance dates</div>
              <div className="mt-1 text-xs text-slate-500">New active lease and insurance dates will appear here automatically.</div>
            </div>
          )}
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><SafeIcon icon="Home" /></div>
              <div>
                <h2 className="text-base font-bold text-slate-900">Properties</h2>
                <p className="text-[11px] text-slate-500">Position and current performance</p>
              </div>
            </div>
            <button type="button" onClick={() => navigate('/properties')} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">View All <SafeIcon icon="ArrowRight" /></button>
          </div>
          {propertySummaries.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {propertySummaries.slice(0, 4).map(({ property, summary }) => (
                <MobilePropertyOverviewCard key={property.id} property={property} summary={summary} onOpen={() => navigate(`/properties/${property.id}`)} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-7 text-center text-sm text-slate-500">No properties in this dashboard scope.</div>
          )}
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-600"><SafeIcon icon="TrendingUp" /></div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Quick Actions</h2>
              <p className="text-[11px] text-slate-500">Jump straight to the work area you need</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {[
              { label: 'Dates', icon: 'Calendar', path: '/important-dates' },
              { label: 'Properties', icon: 'Home', path: '/properties' },
              { label: 'Tenants', icon: 'Users', path: '/tenants' },
              { label: 'Documents', icon: 'FileText', path: '/documents' },
              { label: 'Reports', icon: 'PieChart', path: '/reports' }
            ].map(action => (
              <button key={action.path} type="button" onClick={() => navigate(action.path)} className="min-h-[72px] rounded-2xl border border-slate-200 bg-slate-50/70 px-2 py-3 text-center transition-colors hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500">
                <SafeIcon icon={action.icon} className="mx-auto text-xl text-amber-600" />
                <div className="mt-2 truncate text-[10px] font-semibold text-slate-700 sm:text-xs">{action.label}</div>
              </button>
            ))}
          </div>
        </section>

        <details className="group rounded-3xl border border-slate-200 bg-white shadow-sm">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600"><SafeIcon icon="Filter" /></div>
              <div>
                <div className="text-sm font-bold text-slate-900">Filters & Exports</div>
                <div className="text-[11px] text-slate-500">Property, FY, transaction filters and reports</div>
              </div>
            </div>
            <SafeIcon icon="ChevronDown" className="text-slate-400 transition-transform group-open:rotate-180" />
          </summary>
          <div className="border-t border-slate-100 p-4 sm:p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Property</label>
                <select value={filters.propertyId} onChange={(e) => setFilters(prev => ({ ...prev, propertyId: e.target.value, spaceId: 'All' }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="All">All Properties</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Financial Year</label>
                <select value={filters.financialYear} onChange={(e) => setFilters(prev => ({ ...prev, financialYear: e.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="All">All Years (charts only)</option>
                  {availableFinancialYears.map(financialYear => <option key={financialYear} value={financialYear}>{financialYear}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Transaction Type</label>
                <select value={filters.transactionType} onChange={(e) => setFilters(prev => ({ ...prev, transactionType: e.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="All">All Types</option><option value="income">Income</option><option value="expense">Expense</option><option value="deduction">Deduction</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Category</label>
                <select value={filters.category} onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="All">All Categories</option>
                  {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Status</label>
                <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
                  <option value="All">All Statuses</option><option value="paid">Paid</option><option value="unpaid">Unpaid</option><option value="partial">Partial</option>
                </select>
              </div>
              <label className="flex min-h-[44px] items-center gap-2 self-end text-sm text-slate-600">
                <input type="checkbox" checked={filters.includeArchived} onChange={(e) => setFilters(prev => ({ ...prev, includeArchived: e.target.checked }))} className="rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
                Include Historical
              </label>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-4 sm:flex sm:flex-wrap sm:justify-end">
              <button onClick={openHistoricalSummaryModal} className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-semibold text-amber-800 sm:col-auto">Add Historical FY Summary</button>
              <button onClick={handleExportCSV} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700">Export CSV</button>
              <button onClick={handleDownloadPDF} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2.5 text-xs font-semibold text-white"><SafeIcon icon="Download" /> PDF</button>
              <button onClick={() => setFilters({ propertyId: 'All', spaceId: 'All', tenantId: 'All', financialYear: calculations.getAustralianFinancialYear(new Date()), startDate: null, endDate: null, transactionType: 'All', category: 'All', status: 'All', includeArchived: false })} className="col-span-2 rounded-lg px-3 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 sm:col-auto">Clear Filters</button>
            </div>
          </div>
        </details>

        {portfolioSummary.dataQualityIssues?.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="flex items-start gap-2">
              <SafeIcon icon="AlertTriangle" className="mt-0.5 text-amber-600" />
              <div>
                <div className="text-sm font-semibold text-amber-900">Calculation review required</div>
                <div className="mt-1 text-xs leading-5 text-amber-800">{portfolioSummary.dataQualityIssues[0]}{portfolioSummary.dataQualityIssues.length > 1 ? ` + ${portfolioSummary.dataQualityIssues.length - 1} more review item(s).` : ''}</div>
              </div>
            </div>
          </div>
        )}

        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <button
            type="button"
            onClick={togglePortfolioAnalysis}
            aria-expanded={isPortfolioAnalysisOpen}
            className="flex min-h-[64px] w-full items-center justify-between gap-3 rounded-3xl p-4 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500 sm:p-5"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><SafeIcon icon="PieChart" /></div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-900">Portfolio Analysis</div>
                <div className={`mt-0.5 text-[11px] ${hasAnyPortfolioAnalysisData ? 'text-slate-500' : 'text-amber-700'}`}>{portfolioAnalysisStatusText}</div>
              </div>
            </div>
            <SafeIcon icon="ChevronDown" className={`shrink-0 text-slate-400 transition-transform ${isPortfolioAnalysisOpen ? 'rotate-180' : ''}`} />
          </button>
          {isPortfolioAnalysisOpen && (
            <div className="space-y-4 border-t border-slate-100 p-3 sm:p-5">
              <div>
                <h3 className="mb-3 text-sm font-bold text-slate-900">Detailed Property Performance</h3>
                {propertySummaries.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {propertySummaries.map(({ property, summary, previousSummary }) => (
                      <PropertySnapshotCard
                        key={property.id}
                        property={property}
                        summary={summary}
                        previousSummary={previousSummary}
                        financialYear={selectedFinancialYear}
                        previousFinancialYear={previousFinancialYear}
                        isCurrentFinancialYear={isSelectedCurrentFinancialYear}
                        onOpen={() => navigate(`/properties/${property.id}`)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-7 text-center text-sm text-slate-500">No property performance records in this scope.</div>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <DashboardChart compact option={incomeVsExpensesChartOption} hasData={incomeVsExpensesHasData} chartKey={`mobile-income-${refreshTrigger}-${filters.propertyId}-${filters.financialYear}`} emptyMessage={historicalSummaryTotals.hasData ? 'Historical FY summaries are annual aggregates. Select the individual property and financial year to view the annual aggregate chart.' : 'No Paid operating income or operating-expense transactions are recorded for this selection.'} />
                <DashboardChart compact option={propertyPerformanceChartOption} hasData={propertyPerformanceHasData} chartKey={`mobile-performance-${refreshTrigger}-${filters.propertyId}-${selectedFinancialYear}`} emptyMessage={`No non-zero cash flow after finance is available for FY ${selectedFinancialYear}.`} />
                <DashboardChart compact option={outgoingsChartOption} hasData={outgoingsChartHasData} chartKey={`mobile-outgoings-${refreshTrigger}-${filters.propertyId}-${filters.financialYear}`} emptyMessage="No Paid operating expenses are recorded for this scope." />
                <DashboardChart compact option={openItemsChartOption} hasData={openItemsChartHasData} chartKey={`mobile-open-items-${refreshTrigger}-${filters.propertyId}`} emptyMessage="There are no open/in-progress/waiting property items in this scope." />
              </div>
              {filters.propertyId !== 'All' && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 text-sm font-bold text-slate-900">Selected Property Summary</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-xl bg-white p-3"><div className="text-lg font-bold text-slate-900">{insuranceSummary.active}</div><div className="text-[10px] text-slate-500">Active Insurance</div></div>
                    <div className="rounded-xl bg-white p-3"><div className="text-lg font-bold text-slate-900">{documentSummary.active}</div><div className="text-[10px] text-slate-500">Active Documents</div></div>
                    <div className="rounded-xl bg-white p-3"><div className="text-lg font-bold text-slate-900">{inspectionSummary.completed}</div><div className="text-[10px] text-slate-500">Inspections Done</div></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <div className="hidden xl:block">
        <div className="space-y-4">
          <section className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            {desktopHeroImage ? (
              <div className="pointer-events-none absolute inset-y-0 right-0 w-[48%] overflow-hidden">
                <img src={desktopHeroImage} alt="" className="h-full w-full object-cover opacity-20" />
                <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent" />
              </div>
            ) : null}
            <div className="relative z-10 flex items-end justify-between gap-6">
              <div>
                <div className="text-sm font-semibold text-amber-700">{'Welcome back' + (desktopFirstName ? ', ' + desktopFirstName : '')}</div>
                <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-950">Here’s your property portfolio at a glance.</h1>
                <p className="mt-1 text-xs text-slate-500">{desktopScopeLabel} · FY {selectedFinancialYear} · {portfolioSummary.totalProperties} {portfolioSummary.totalProperties === 1 ? 'property' : 'properties'}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">FY {selectedFinancialYear}</span>
                <button type="button" onClick={() => navigate('/reports')} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50">
                  <SafeIcon icon="BarChart2" /> View Reports
                </button>
              </div>
            </div>
          </section>

          <details className="group rounded-xl border border-slate-200 bg-white shadow-sm">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600"><SafeIcon icon="Sliders" /></div>
                <div>
                  <div className="text-sm font-semibold text-slate-800">Dashboard Filters & Exports</div>
                  <div className="text-[10px] text-slate-500">Property, financial year, transaction filters, CSV and PDF</div>
                </div>
              </div>
              <SafeIcon icon="ChevronDown" className="text-slate-400 transition-transform group-open:rotate-180" />
            </summary>
            <div className="border-t border-slate-100 p-4">
              <div className="grid grid-cols-6 gap-3">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">Property</label>
                  <select value={filters.propertyId} onChange={(e) => setFilters(prev => ({ ...prev, propertyId: e.target.value, spaceId: 'All' }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500">
                    <option value="All">All Properties</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">Financial Year</label>
                  <select value={filters.financialYear} onChange={(e) => setFilters(prev => ({ ...prev, financialYear: e.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500">
                    <option value="All">All Years (charts only)</option>
                    {availableFinancialYears.map(financialYear => <option key={financialYear} value={financialYear}>{financialYear}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">Transaction Type</label>
                  <select value={filters.transactionType} onChange={(e) => setFilters(prev => ({ ...prev, transactionType: e.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500">
                    <option value="All">All Types</option>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                    <option value="deduction">Deduction</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">Category</label>
                  <select value={filters.category} onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500">
                    <option value="All">All Categories</option>
                    {uniqueCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">Status</label>
                  <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500">
                    <option value="All">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>
                <label className="flex min-h-[38px] items-center gap-2 self-end text-xs text-slate-600">
                  <input type="checkbox" checked={filters.includeArchived} onChange={(e) => setFilters(prev => ({ ...prev, includeArchived: e.target.checked }))} className="rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
                  Include Historical
                </label>
              </div>
              <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
                <button onClick={openHistoricalSummaryModal} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100">Add Historical FY Summary</button>
                <button onClick={handleExportCSV} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Export CSV</button>
                <button onClick={handleDownloadPDF} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"><SafeIcon icon="Download" /> Download PDF</button>
                <button onClick={() => setFilters({ propertyId: 'All', spaceId: 'All', tenantId: 'All', financialYear: calculations.getAustralianFinancialYear(new Date()), startDate: null, endDate: null, transactionType: 'All', category: 'All', status: 'All', includeArchived: false })} className="rounded-lg px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100">Clear Filters</button>
              </div>
            </div>
          </details>

          <section className="grid grid-cols-3 gap-3 2xl:grid-cols-6">
            <DesktopKpiCard icon="Home" label="Portfolio Market Value" value={formatDashboardCurrency(portfolioSummary.totalMarketValue)} supportingText={'Acquisition ' + formatDashboardCurrency(portfolioSummary.totalAcquisitionCost)} tone="amber" />
            <DesktopKpiCard icon="Layers" label="Total Equity" value={formatDashboardCurrency(portfolioSummary.totalEquity)} supportingText={'LVR ' + calculations.formatPercentage(portfolioSummary.portfolioLVR || 0)} tone="amber" />
            <DesktopKpiCard icon="BarChart2" label="Annual Gross Rent" value={formatDashboardCurrency(portfolioSummary.annualRent)} supportingText="Expected contractual rent" tone="amber" />
            <DesktopKpiCard icon="DollarSign" label="Cash Flow After Finance" value={formatSignedDashboardCurrency(portfolioSummary.cashFlowAfterFinance)} supportingText={'FY ' + portfolioSummary.currentFinancialYear} tone={portfolioSummary.cashFlowAfterFinance >= 0 ? 'emerald' : 'red'} />
            <DesktopKpiCard icon="Users" label="Occupancy" value={calculations.formatPercentage(portfolioSummary.occupancyRate || 0)} supportingText={portfolioSummary.occupiedSpaces + ' of ' + portfolioSummary.totalSpaces + ' spaces'} tone="emerald" />
            <DesktopKpiCard icon="Clipboard" label="Open Items" value={mobileOpenItemsCount} supportingText={mobileOpenItemsCount === 1 ? 'Active item' : 'Active items'} tone={mobileOpenItemsCount > 0 ? 'amber' : 'slate'} />
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
            <div className="mb-3 flex items-end justify-between gap-4">
              <div>
                <h2 className="text-sm font-bold text-slate-900">Current Projected Position</h2>
                <p className="mt-0.5 text-[10px] text-slate-500">Current rents and current recorded loan rates/terms. Property scope is dynamic; selected FY remains the basis for recorded performance and trend comparisons.</p>
              </div>
              <span className="text-[10px] font-medium text-slate-400">{filters.propertyId==='All' ? 'All dashboard properties' : 'Selected property'}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-6">
              <DesktopKpiCard icon="Home" label="PPR Current Installment" value={currentProjectionSummary.pprProjectionComplete ? formatDashboardCurrency(currentProjectionSummary.pprMonthlyInstallment) : 'Needs data'} supportingText={currentProjectionSummary.pprProjectionComplete ? `${formatDashboardCurrency(currentProjectionSummary.pprAnnualInstallment)} / year` : `${currentProjectionSummary.incompletePprLoanProperties} PPR propert${currentProjectionSummary.incompletePprLoanProperties===1 ? 'y' : 'ies'} missing complete loan inputs`} tone={currentProjectionSummary.pprProjectionComplete ? 'blue' : 'amber'} />
              <DesktopKpiCard icon="CreditCard" label="Investment Installments" value={currentProjectionSummary.incompleteInvestmentLoanProperties===0 ? formatDashboardCurrency(currentProjectionSummary.investmentMonthlyInstallment) : 'Needs data'} supportingText={currentProjectionSummary.incompleteInvestmentLoanProperties===0 ? `${formatDashboardCurrency(currentProjectionSummary.investmentAnnualInstallment)} / year` : `${currentProjectionSummary.incompleteInvestmentLoanProperties} investment propert${currentProjectionSummary.incompleteInvestmentLoanProperties===1 ? 'y' : 'ies'} missing complete loan inputs`} tone="amber" />
              <DesktopKpiCard icon="BarChart2" label="Anticipated Gross Rent" value={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? formatDashboardCurrency(currentProjectionSummary.anticipatedMonthlyGrossRent) : 'Needs data'} supportingText={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? `${formatDashboardCurrency(currentProjectionSummary.anticipatedAnnualGrossRent)} / year · before recorded deductions` : `${currentProjectionSummary.incompleteInvestmentRentProperties} investment propert${currentProjectionSummary.incompleteInvestmentRentProperties===1 ? 'y' : 'ies'} missing complete rent inputs`} tone={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? 'blue' : 'amber'} />
              <DesktopKpiCard icon="DollarSign" label="Anticipated Net Rent" value={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? formatDashboardCurrency(currentProjectionSummary.anticipatedMonthlyNetRent) : 'Needs data'} supportingText={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? `${formatDashboardCurrency(currentProjectionSummary.anticipatedAnnualNetRent)} / year · recorded schedule deductions` : 'Complete current rent data is required for every investment property in scope'} tone={currentProjectionSummary.incompleteInvestmentRentProperties===0 ? 'emerald' : 'amber'} />
              <DesktopKpiCard icon="TrendingUp" label="Projected Investment Net Position" value={currentProjectionSummary.investmentProjectionComplete ? formatSignedDashboardCurrency(currentProjectionSummary.projectedInvestmentMonthlyPosition) : 'Needs data'} supportingText={currentProjectionSummary.investmentProjectionComplete ? `${formatDashboardCurrency(currentProjectionSummary.anticipatedMonthlyNetRent)} net rent - ${formatDashboardCurrency(currentProjectionSummary.investmentMonthlyInstallment)} instalments · ${formatSignedDashboardCurrency(currentProjectionSummary.projectedInvestmentAnnualPosition)} / year` : `${currentProjectionSummary.incompleteInvestmentProperties} investment propert${currentProjectionSummary.incompleteInvestmentProperties===1 ? 'y' : 'ies'} incomplete`} tone={!currentProjectionSummary.investmentProjectionComplete ? 'amber' : currentProjectionSummary.projectedInvestmentMonthlyPosition >= 0 ? 'emerald' : 'red'} />
              <DesktopKpiCard icon="Activity" label="Investment Property Mix" value={currentProjectionSummary.investmentPropertyCount ? `${currentProjectionSummary.positiveProperties} positive` : 'No investments'} supportingText={currentProjectionSummary.investmentPropertyCount ? `${currentProjectionSummary.negativeProperties} negative · ${currentProjectionSummary.neutralProperties} neutral${currentProjectionSummary.incompleteInvestmentProperties ? ` · ${currentProjectionSummary.incompleteInvestmentProperties} incomplete` : ''}` : 'No investment properties in this scope'} tone={currentProjectionSummary.negativeProperties > 0 ? 'amber' : 'slate'} />
            </div>
            <p className="mt-3 rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2 text-[10px] leading-4 text-amber-900"><strong>Projected only:</strong> investment net position = anticipated monthly net rent less current investment-loan instalments. Instalments use the currently recorded loan balances, interest rates, repayment types and remaining terms; interest-rate or loan changes can change the projection. Anticipated net rent uses current contractual rent and recorded management or rent-schedule deductions; rent increases, vacancies and additional expenses can change it. PPR instalments are shown separately and are excluded from the investment net position. Unrecorded council rates, water, strata, insurance, maintenance and other ownership costs are excluded. Use Cash Flow After Finance and FY comparisons for recorded historical performance.</p>
          </section>

          <section className="grid grid-cols-12 gap-4">
            <div className="col-span-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-bold text-slate-900">Important Dates</h2>
                  <p className="mt-0.5 text-[10px] text-slate-500">Nearest lease expiries and insurance renewals</p>
                </div>
                <button type="button" onClick={() => navigate('/important-dates')} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">View All <SafeIcon icon="ArrowRight" /></button>
              </div>
              {mobileUpcomingDates.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {mobileUpcomingDates.slice(0, 5).map(event => {
                    const date = new Date(event.date);
                    const day = date.toLocaleDateString('en-AU', { day: '2-digit' });
                    const month = date.toLocaleDateString('en-AU', { month: 'short' }).toUpperCase();
                    const urgency = getDashboardDateUrgency(event.days);
                    return (
                      <button key={event.id} type="button" onClick={() => navigate(event.route)} className="grid w-full grid-cols-[48px_minmax(0,1.15fr)_minmax(135px,0.9fr)_auto_18px] items-center gap-3 py-2 text-left transition-colors hover:bg-slate-50">
                        <div className={`rounded-lg px-1.5 py-1.5 text-center ${urgency.soft}`}>
                          <div className={`text-sm font-bold leading-none ${urgency.text}`}>{day}</div>
                          <div className={`mt-1 text-[9px] font-semibold leading-none ${urgency.text}`}>{month}</div>
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-xs font-semibold text-slate-800" title={event.propertyName}>{event.propertyName}</div>
                          <div className="mt-0.5 truncate text-[10px] text-slate-500">{event.label}</div>
                        </div>
                        <div className="min-w-0 rounded-lg bg-slate-50/70 px-2.5 py-1.5">
                          <div className="truncate text-[10px] font-semibold text-slate-700" title={event.contextPrimary || ''}>{event.contextPrimary || 'Details not recorded'}</div>
                          <div className="mt-0.5 truncate text-[9px] text-slate-500" title={event.contextSecondary || ''}>{event.contextSecondary || (event.type === 'insurance' ? 'Insurance policy' : 'Lease')}</div>
                        </div>
                        <span className={`whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-semibold ${urgency.badge}`}>{event.days === 0 ? 'Today' : 'In ' + event.days + ' days'}</span>
                        <SafeIcon icon="ChevronRight" className="text-slate-300" />
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex min-h-[235px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-center">
                  <SafeIcon icon="Calendar" className="text-2xl text-slate-300" />
                  <div className="mt-2 text-sm font-semibold text-slate-700">No upcoming critical dates</div>
                  <div className="mt-1 text-xs text-slate-500">Active lease and insurance dates will appear here automatically.</div>
                </div>
              )}
            </div>

            <div className="col-span-7 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-bold text-slate-900">Portfolio Snapshot & Key Insights</h2>
                  <p className="mt-0.5 text-[10px] text-slate-500">Live portfolio position from recorded data</p>
                </div>
                <button type="button" onClick={() => navigate('/reports')} className="text-xs font-semibold text-amber-700">View Report</button>
              </div>
              <div className="grid min-h-[245px] grid-cols-[minmax(190px,0.9fr)_minmax(230px,1.1fr)_minmax(190px,0.8fr)] gap-4">
                <div className="relative overflow-hidden rounded-xl bg-slate-900">
                  {desktopHeroImage ? <img src={desktopHeroImage} alt="" className="h-full w-full object-cover opacity-90" /> : <div className="flex h-full min-h-[220px] items-center justify-center bg-slate-100 text-slate-300"><SafeIcon icon="Home" className="text-4xl" /></div>}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 via-slate-950/50 to-transparent p-3 pt-12 text-white">
                    <div className="text-xs font-semibold">{portfolioSummary.totalProperties} {portfolioSummary.totalProperties === 1 ? 'Property' : 'Properties'} in portfolio</div>
                    <div className="mt-0.5 text-[10px] text-slate-200">{desktopStateCount > 0 ? desktopStateCount + (desktopStateCount === 1 ? ' state' : ' states') : 'Portfolio footprint'}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { icon: 'Layers', label: 'Portfolio equity', value: formatDashboardCurrency(portfolioSummary.totalEquity), tone: 'bg-amber-50 text-amber-700' },
                    { icon: 'DollarSign', label: 'Net operating income', value: formatDashboardCurrency(portfolioSummary.netOperatingIncome), tone: 'bg-emerald-50 text-emerald-700' },
                    { icon: 'Users', label: 'Current occupancy', value: calculations.formatPercentage(portfolioSummary.occupancyRate || 0), tone: 'bg-blue-50 text-blue-700' },
                    { icon: 'TrendingUp', label: 'Net yield', value: ((portfolioSummary.netYield || 0) * 100).toFixed(2) + '%', tone: 'bg-violet-50 text-violet-700' }
                  ].map(insight => (
                    <div key={insight.label} className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-slate-50">
                      <div className={'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ' + insight.tone}><SafeIcon icon={insight.icon} /></div>
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold text-slate-800">{insight.label}</div>
                        <div className="mt-0.5 truncate text-[10px] text-slate-500">{insight.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <SafeIcon icon="Activity" className="text-xl text-amber-600" />
                  <div className="mt-3 text-xs font-semibold leading-5 text-slate-800">Portfolio overview</div>
                  <p className="mt-2 text-[11px] leading-5 text-slate-600">
                    Current equity is {formatDashboardCurrency(portfolioSummary.totalEquity)} with {calculations.formatPercentage(portfolioSummary.occupancyRate || 0)} occupancy.
                    Cash flow after finance for FY {portfolioSummary.currentFinancialYear} is {formatSignedDashboardCurrency(portfolioSummary.cashFlowAfterFinance)}.
                  </p>
                  <div className="mt-3 h-0.5 w-8 rounded-full bg-amber-500" />
                  <div className="mt-3 text-[10px] font-medium text-slate-500">Figures reflect the selected dashboard scope and recorded application data.</div>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" aria-labelledby="desktop-property-performance-heading">
            <div className="mb-3 flex items-end justify-between gap-4">
              <div>
                <h2 id="desktop-property-performance-heading" className="text-base font-bold text-slate-900">Property Performance</h2>
                <p className="mt-0.5 text-[10px] text-slate-500">Primary residence is pinned first. Drag investment cards to save your preferred dashboard order on this device.</p>
              </div>
              <button type="button" onClick={() => navigate('/properties')} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">View All Properties <SafeIcon icon="ArrowRight" /></button>
            </div>
            {orderedPropertySummaries.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 2xl:grid-cols-4">
                {orderedPropertySummaries.map(({ property, summary }) => {
                  const pinned = isOwnerOccupiedProperty(property, getPropertyFinancialDetails(property).loanType);
                  return (
                    <DesktopPropertyPerformanceCard
                      key={property.id}
                      property={property}
                      summary={summary}
                      pinned={pinned}
                      draggable={!pinned}
                      isDragging={String(draggedPropertyId || '') === String(property.id)}
                      onOpen={() => navigate('/properties/' + property.id)}
                      onDragStart={(event) => handlePropertyDragStart(event, property.id)}
                      onDragEnd={() => setDraggedPropertyId(null)}
                      onDragOver={(event) => { if (!pinned) { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; } }}
                      onDrop={(event) => handlePropertyDrop(event, property.id)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500">No properties in this dashboard scope.</div>
            )}
          </section>

          {portfolioSummary.dataQualityIssues?.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <SafeIcon icon="AlertTriangle" className="mt-0.5 text-amber-600" />
                <div>
                  <div className="text-sm font-semibold text-amber-900">Calculation review required</div>
                  <div className="mt-1 text-xs text-amber-800">{portfolioSummary.dataQualityIssues[0]}{portfolioSummary.dataQualityIssues.length > 1 ? ' + ' + (portfolioSummary.dataQualityIssues.length - 1) + ' more review item(s).' : ''}</div>
                </div>
              </div>
            </div>
          )}

          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <button
              type="button"
              onClick={togglePortfolioAnalysis}
              aria-expanded={isPortfolioAnalysisOpen}
              className="flex min-h-[58px] w-full items-center justify-between gap-4 rounded-xl px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><SafeIcon icon="PieChart" /></div>
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-slate-900">Portfolio Analysis</h2>
                  <p className={`mt-0.5 text-[10px] ${hasAnyPortfolioAnalysisData ? 'text-slate-500' : 'text-amber-700'}`}>{portfolioAnalysisStatusText}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="hidden text-[10px] font-medium text-slate-400 sm:inline">{isPortfolioAnalysisOpen ? 'Collapse' : 'Expand'}</span>
                <SafeIcon icon="ChevronDown" className={`text-slate-400 transition-transform ${isPortfolioAnalysisOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {isPortfolioAnalysisOpen && (
              <div className="border-t border-slate-100 p-4">
                <div className="grid grid-cols-2 gap-4 2xl:grid-cols-3">
                  <DashboardChart compact option={incomeVsExpensesChartOption} hasData={incomeVsExpensesHasData} chartKey={'desktop-income-' + refreshTrigger + '-' + filters.propertyId + '-' + filters.financialYear} emptyMessage={historicalSummaryTotals.hasData ? 'Historical FY summaries are annual aggregates. Select the individual property and financial year to view the annual aggregate chart.' : 'No Paid operating income or operating-expense transactions are recorded for this selection.'} />
                  <DashboardChart compact option={propertyPerformanceChartOption} hasData={propertyPerformanceHasData} chartKey={'desktop-performance-' + refreshTrigger + '-' + filters.propertyId + '-' + selectedFinancialYear} emptyMessage={'No non-zero cash flow after finance is available for FY ' + selectedFinancialYear + '.'} />
                  <DashboardChart compact option={outgoingsChartOption} hasData={outgoingsChartHasData} chartKey={'desktop-outgoings-' + refreshTrigger + '-' + filters.propertyId + '-' + filters.financialYear} emptyMessage="No Paid operating expenses are recorded for this scope." />
                  <DashboardChart compact option={openItemsChartOption} hasData={openItemsChartHasData} chartKey={'desktop-open-items-' + refreshTrigger + '-' + filters.propertyId} emptyMessage="There are no open/in-progress/waiting property items in this scope." />
                </div>
              </div>
            )}
          </section>

          <section aria-labelledby="detailed-property-cards-heading">
            <div className="mb-3 flex items-end justify-between gap-4">
              <div>
                <h2 id="detailed-property-cards-heading" className="text-base font-bold text-slate-900">Detailed Property Cards</h2>
                <p className="mt-0.5 text-[10px] text-slate-500">The detailed property cards are retained below the executive dashboard. Their order follows the draggable Property Performance strip above.</p>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[10px] text-slate-400"><SafeIcon icon="Move" /> Drag investment cards to reorder</div>
            </div>
            {orderedPropertySummaries.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 2xl:grid-cols-3">
                {orderedPropertySummaries.map(({ property, summary, previousSummary }) => {
                  const pinned = isOwnerOccupiedProperty(property, getPropertyFinancialDetails(property).loanType);
                  return (
                    <div
                      key={property.id}
                      draggable={!pinned}
                      onDragStart={(event) => handlePropertyDragStart(event, property.id)}
                      onDragEnd={() => setDraggedPropertyId(null)}
                      onDragOver={(event) => { if (!pinned) { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; } }}
                      onDrop={(event) => handlePropertyDrop(event, property.id)}
                      className={String(draggedPropertyId || '') === String(property.id) ? 'opacity-60' : ''}
                    >
                      <PropertySnapshotCard
                        property={property}
                        summary={summary}
                        previousSummary={previousSummary}
                        financialYear={selectedFinancialYear}
                        previousFinancialYear={previousFinancialYear}
                        isCurrentFinancialYear={isSelectedCurrentFinancialYear}
                        onOpen={() => navigate('/properties/' + property.id)}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
                <SafeIcon icon="Home" className="mx-auto text-3xl text-slate-300" />
                <div className="mt-3 text-sm font-semibold text-slate-700">No properties in this dashboard scope</div>
                <div className="mt-1 text-xs text-slate-500">Adjust the property or historical filters to display property snapshots.</div>
              </div>
            )}
          </section>

          {filters.propertyId !== 'All' && (
            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">Selected Property Summary</h3>
              <div className="mt-3 grid grid-cols-3 gap-4">
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Insurance</div>
                  <div className="mt-2 flex items-center justify-between text-xs"><span className="text-slate-500">Active</span><span className="font-bold text-emerald-600">{insuranceSummary.active}</span></div>
                  <div className="mt-1 flex items-center justify-between text-xs"><span className="text-slate-500">Expiring soon</span><span className="font-semibold text-amber-600">{insuranceSummary.expiringSoon}</span></div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Documents</div>
                  <div className="mt-2 flex items-center justify-between text-xs"><span className="text-slate-500">Total</span><span className="font-bold text-slate-800">{documentSummary.total}</span></div>
                  <div className="mt-1 flex items-center justify-between text-xs"><span className="text-slate-500">Active</span><span className="font-semibold text-emerald-600">{documentSummary.active}</span></div>
                </div>
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Inspections</div>
                  <div className="mt-2 flex items-center justify-between text-xs"><span className="text-slate-500">Completed</span><span className="font-bold text-emerald-600">{inspectionSummary.completed}</span></div>
                  <div className="mt-1 flex items-center justify-between text-xs"><span className="text-slate-500">With issues</span><span className="font-semibold text-amber-600">{inspectionSummary.withIssues}</span></div>
                </div>
              </div>
            </section>
          )}

          <section className="grid grid-cols-5 gap-3">
            <button onClick={() => navigate('/important-dates')} className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center transition-all hover:bg-amber-100 hover:shadow-md"><SafeIcon icon="Calendar" className="mx-auto text-xl text-amber-600" /><div className="mt-1.5 text-xs font-semibold text-amber-800">Important Dates</div></button>
            <button onClick={() => navigate('/properties')} className="rounded-xl border border-slate-200 bg-white p-3 text-center transition-shadow hover:shadow-md"><SafeIcon icon="Home" className="mx-auto text-xl text-amber-600" /><div className="mt-1.5 text-xs font-semibold text-slate-700">Properties</div></button>
            <button onClick={() => navigate('/tenants')} className="rounded-xl border border-slate-200 bg-white p-3 text-center transition-shadow hover:shadow-md"><SafeIcon icon="Users" className="mx-auto text-xl text-amber-600" /><div className="mt-1.5 text-xs font-semibold text-slate-700">Tenants</div></button>
            <button onClick={() => navigate('/transactions')} className="rounded-xl border border-slate-200 bg-white p-3 text-center transition-shadow hover:shadow-md"><SafeIcon icon="DollarSign" className="mx-auto text-xl text-amber-600" /><div className="mt-1.5 text-xs font-semibold text-slate-700">Transactions</div></button>
            <button onClick={() => navigate('/reports')} className="rounded-xl border border-slate-200 bg-white p-3 text-center transition-shadow hover:shadow-md"><SafeIcon icon="FileText" className="mx-auto text-xl text-amber-600" /><div className="mt-1.5 text-xs font-semibold text-slate-700">Reports</div></button>
          </section>
        </div>
      </div>

      <Modal isOpen={isHistoricalSummaryOpen} onClose={() => !isSavingHistoricalSummary && setIsHistoricalSummaryOpen(false)} title="Add Historical Financial Year Summary" maxWidth="max-w-3xl">
        <form onSubmit={handleHistoricalSummarySubmit} className="space-y-5">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-semibold text-amber-900">One entry for a completed financial year</div>
            <p className="text-xs text-amber-800 mt-1 leading-5">
              Use totals from your agent/lender statements. MPP stores the totals as a small set of auditable aggregate ledger records without changing the backend schema. Operating expenses affect NOI; loan interest is kept separate and affects finance cash flow, not NOI.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Property *</label>
              <select value={historicalSummaryForm.propertyId} onChange={(e) => setHistoricalSummaryForm(prev => ({ ...prev, propertyId: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" required>
                <option value="">Select property...</option>
                {historicalEntryProperties.map(property => <option key={property.id} value={property.id}>{property.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Completed Financial Year *</label>
              <select value={historicalSummaryForm.financialYear} onChange={(e) => setHistoricalSummaryForm(prev => ({ ...prev, financialYear: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" required>
                {HISTORICAL_FY_OPTIONS.map(financialYear => <option key={financialYear} value={financialYear}>{financialYear}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Total Rent / Operating Income Received ($)</label>
              <input type="number" min="0" step="0.01" value={historicalSummaryForm.income} onChange={(e) => setHistoricalSummaryForm(prev => ({ ...prev, income: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="0.00" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Total Property Operating Expenses Paid ($)</label>
              <input type="number" min="0" step="0.01" value={historicalSummaryForm.operatingExpenses} onChange={(e) => setHistoricalSummaryForm(prev => ({ ...prev, operatingExpenses: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="0.00" />
              <p className="text-[11px] text-slate-500 mt-1">Exclude loan interest, principal repayments, capital expenditure, bond movements and owner transfers/disbursements.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Total Loan Interest Paid ($)</label>
              <input type="number" min="0" step="0.01" value={historicalSummaryForm.loanInterest} onChange={(e) => setHistoricalSummaryForm(prev => ({ ...prev, loanInterest: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="0.00" />
              <p className="text-[11px] text-slate-500 mt-1">Interest is financing: it is excluded from NOI and shown separately in cash flow after finance.</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Source / Notes</label>
              <textarea value={historicalSummaryForm.notes} onChange={(e) => setHistoricalSummaryForm(prev => ({ ...prev, notes: e.target.value }))} rows="3" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="e.g. Annual agent statement + lender interest statement" />
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 leading-5">
            Do not enter the agent's headline “Money Out” if it includes amounts transferred to you, bond transfers, principal repayments or capital purchases. Enter only genuine property operating expenses in the operating-expense field above.
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button type="button" onClick={() => setIsHistoricalSummaryOpen(false)} disabled={isSavingHistoricalSummary} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={isSavingHistoricalSummary} className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg disabled:opacity-50">
              {isSavingHistoricalSummary ? 'Saving Summary...' : 'Save Historical FY Summary'}
            </button>
          </div>
        </form>
      </Modal>

    </div>
  );
};

export default DashboardPage;